import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, Table, Spinner, Badge, Nav, Modal, Form, ProgressBar } from "react-bootstrap";
import * as bootstrap from "bootstrap";
import { useParams } from "react-router-dom";
import { getApi, postApi, putApi, deleteApi } from "../../../shared/services/apiHelper";
import { runCpm, getCpmResult, projectDetail, estimationRunMaterialSummaryJson, estimationRunCostSummaryJson, estimationRunLabourSummaryJson, saveMaterialProcurement, saveActualCost, saveLabourAllocation, updateActivityActualStart, updateActivityActualFinish, updateProjectWorkStart, projectMilestones, projectMilestoneDetail, milestoneTasks, milestoneTaskUnlink } from "../../../shared/settings/apiConfig";
import moment from "moment";
import JSZip from "jszip";
import { saveFileWithPrompt } from "../../../shared/utils/downloadUtils";
import AssessmentResultModal from './AssessmentResultModal';
import assessmentResults from './result.json';

import './ProjectCPM.scss';

export default function ProjectCPM(props) {
    const { id: projectId } = useParams();
    
    const [loading, setLoading] = useState(true);
    const [cpmData, setCpmData] = useState(null);
    const [projectData, setProjectData] = useState(null);
    const [error, setError] = useState(null);
    const [filterStage, setFilterStage] = useState("all");
    const [procurementFilterStage, setProcurementFilterStage] = useState("all");
    const [activeTab, setActiveTab] = useState("milestones");
    const [expandedStageDiagram, setExpandedStageDiagram] = useState(null);
    const [showResetModal, setShowResetModal] = useState(false);
    const [workStarted, setWorkStarted] = useState(null);
    const [workStartDate, setWorkStartDate] = useState(null);
    const [newStartDate, setNewStartDate] = useState("");
    const [newEndDate, setNewEndDate] = useState("");
    const [validated, setValidated] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [downloadSuccess, setDownloadSuccess] = useState(null);
    
    // ── Procurement Tab State ───────────────────────────────────────────────────
    const [procurementSections, setProcurementSections] = useState(null);
    const [procurementTotal, setProcurementTotal] = useState(null);
    const [materialColumns, setMaterialColumns] = useState([]);
    const [procurementLoading, setProcurementLoading] = useState(true);
    const [procurementError, setProcurementError] = useState(null);
    
    // ── Labour Tab State ────────────────────────────────────────────────────────
    const [labourSections, setLabourSections] = useState(null);
    const [labourColumns, setLabourColumns] = useState([]);
    const [labourTotal, setLabourTotal] = useState(null);
    const [labourLoading, setLabourLoading] = useState(false);
    const [labourError, setLabourError] = useState(null);
    const [labourAllocated, setLabourAllocated] = useState({});
    const [labourFilterStage, setLabourFilterStage] = useState("all");
    const [availableLabour, setAvailableLabour] = useState({
        helper: 0,
        mason: 0,
        mixerOperator: 0,
        barBender: 0,
        carpenter: 0
    });
    const [availableLabourSaving, setAvailableLabourSaving] = useState(false);
    const [availableLabourEdit, setAvailableLabourEdit] = useState({
        helper: 0,
        mason: 0,
        mixerOperator: 0,
        barBender: 0,
        carpenter: 0
    });
    const [isEditingAvailableLabour, setIsEditingAvailableLabour] = useState(false);
    
    // ── Utility function to normalize labour type names ────────────────────────
    const normalizeLabourType = (labourType) => {
        if (!labourType) return '';
        const normalized = labourType.toLowerCase().replace(/\s+/g, '');
        // Handle special cases
        if (normalized === 'barbender') return 'barBender';
        // Note: "Mixer Operator" from API should map to combined mixer+operator
        if (normalized === 'mixeroperator') return 'mixerOperator';
        return normalized;
    };
    
    // Helper to get available labour count for a normalized labour type
    const getAvailableLabourCount = (normalizedKey, availableLabourObj) => {
        // mixerOperator is stored as combined value in state
        return availableLabourObj[normalizedKey] || 0;
    };
     
    // ── Labour Edit Mode State ────────────────────────────────────────────────
    const [isEditingLabour, setIsEditingLabour] = useState(false);
    const [labourEditData, setLabourEditData] = useState({});
    const [labourSaving, setLabourSaving] = useState(false);
    const [labourSaveSuccess, setLabourSaveSuccess] = useState(null);
    const [showAllocationErrorModal, setShowAllocationErrorModal] = useState(false);
    const [allocationErrorMessage, setAllocationErrorMessage] = useState("");
    
    // ── Procurement Edit Mode State ─────────────────────────────────────────────
    const [isEditingProcurement, setIsEditingProcurement] = useState(false);
    const [procurementEditData, setProcurementEditData] = useState({});
    const [procurementSaving, setProcurementSaving] = useState(false);
    const [procurementSaveSuccess, setProcurementSaveSuccess] = useState(null);

    // ── Milestone Tab State ──────────────────────────────────────────────────────
    const [milestones, setMilestones] = useState([]);
    const [milestonesLoading, setMilestonesLoading] = useState(false);
    const [milestonesError, setMilestonesError] = useState(null);
    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [editingMilestone, setEditingMilestone] = useState(null);
    const [milestoneForm, setMilestoneForm] = useState({
        name: "",
        description: "",
        order: "",
        start_date: "",
        end_date: ""
    });
    const [showDeleteMilestoneModal, setShowDeleteMilestoneModal] = useState(false);
    const [milestoneToDelete, setMilestoneToDelete] = useState(null);
    const [showUnlinkTaskModal, setShowUnlinkTaskModal] = useState(false);
    const [taskToUnlink, setTaskToUnlink] = useState(null);
    const [milestoneFormValidated, setMilestoneFormValidated] = useState(false);
    const [dateValidationError, setDateValidationError] = useState("");
    const [milestoneSaving, setMilestoneSaving] = useState(false);
    const [showTaskLinkModal, setShowTaskLinkModal] = useState(false);
    const [selectedMilestoneForTasks, setSelectedMilestoneForTasks] = useState(null);
    const [availableTasks, setAvailableTasks] = useState([]);
    const [selectedTaskIds, setSelectedTaskIds] = useState([]);
    const [taskLinkingSaving, setTaskLinkingSaving] = useState(false);
    const [taskSearchQuery, setTaskSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [milestoneSuccess, setMilestoneSuccess] = useState(null);
    const [expandedMilestones, setExpandedMilestones] = useState({});

    const toggleMilestoneExpansion = (milestoneId) => {
        setExpandedMilestones(prev => ({
            ...prev,
            [milestoneId]: prev[milestoneId] === false ? true : false
        }));
    };

    // ── Milestone Name Uniqueness Validation ────────────────────────────────────
    const validateMilestoneNameUniqueness = (name, existingMilestones, editingMilestone = null) => {
        if (!name || !name.trim()) return true; // Let required validation handle empty names
        
        const trimmedName = name.trim();
        const lowerCaseName = trimmedName.toLowerCase();
        
        return !existingMilestones.some(milestone => 
            milestone.name.toLowerCase() === lowerCaseName && 
            (editingMilestone ? milestone._id !== editingMilestone._id : true)
        );
    };

    // Debounce effect for task search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(taskSearchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [taskSearchQuery]);


    // ── Procurement: Transform API response to table format ───────────────────
    const transformMaterialSummaryToProcurement = (apiData) => {
        if (!apiData || !apiData.floors || apiData.floors.length === 0) return null;

        // Build materialColumns array from the API's columns definition
        const cols = apiData.columns.map(c => `${c.key} (${c.unit})`);
        
        // Build the index map from material column key -> position in array
        const colIndex = {};
        apiData.columns.forEach((c, i) => { colIndex[c.key] = i; });

        const nullRow = () => new Array(cols.length).fill(null);

        // Build sections from the API's floors
        const sections = apiData.floors.map(floor => {
            const stages = floor.modules.map(mod => {
                const estimatedValues = nullRow();
                const procuredValues = nullRow();
                const usedValues = nullRow();
                
                // Map module.totals (estimated - keyed by material name) to the column positions
                if (mod.totals) {
                    Object.entries(mod.totals).forEach(([matKey, qty]) => {
                        const ci = colIndex[matKey];
                        if (ci !== undefined) {
                            estimatedValues[ci] = qty;
                        }
                    });
                }
                
                // Map module.procurement data (from backend) to the column positions
                if (mod.procurement) {
                    if (mod.procurement.procured) {
                        Object.entries(mod.procurement.procured).forEach(([matKey, qty]) => {
                            const ci = colIndex[matKey];
                            if (ci !== undefined) {
                                procuredValues[ci] = qty;
                            }
                        });
                    }
                    if (mod.procurement.used) {
                        Object.entries(mod.procurement.used).forEach(([matKey, qty]) => {
                            const ci = colIndex[matKey];
                            if (ci !== undefined) {
                                usedValues[ci] = qty;
                            }
                        });
                    }
                }
                
                return {
                    name: mod.label,
                    rows: [
                        { label: "Estimated", values: estimatedValues },
                        { label: "Procured", values: procuredValues },
                        { label: "Used", values: usedValues }
                    ]
                };
            });

            return {
                sectionTitle: floor.floor_label.toUpperCase(),
                floorLabel: floor.floor_label,
                stages
            };
        });

        // Build grand total row
        const grandTotalValues = nullRow();
        if (apiData.grand_total) {
            Object.entries(apiData.grand_total).forEach(([matKey, qty]) => {
                const ci = colIndex[matKey];
                if (ci !== undefined) {
                    grandTotalValues[ci] = qty;
                }
            });
        }

        // Calculate grand totals for Procured and Used by summing all modules
        const grandTotalProcured = new Array(cols.length).fill(0);
        const grandTotalUsed = new Array(cols.length).fill(0);
        
        sections.forEach(section => {
            section.stages.forEach(stage => {
                // Get procured and used rows for this stage
                const procuredRow = stage.rows.find(r => r.label === "Procured");
                const usedRow = stage.rows.find(r => r.label === "Used");
                
                if (procuredRow) {
                    procuredRow.values.forEach((val, idx) => {
                        if (val != null && !isNaN(val)) {
                            grandTotalProcured[idx] += Number(val);
                        }
                    });
                }
                
                if (usedRow) {
                    usedRow.values.forEach((val, idx) => {
                        if (val != null && !isNaN(val)) {
                            grandTotalUsed[idx] += Number(val);
                        }
                    });
                }
            });
        });
        
        // Convert 0s to null for display purposes (show "-" instead of "0" when no data)
        const finalProcuredTotals = grandTotalProcured.map(val => val === 0 ? null : val);
        const finalUsedTotals = grandTotalUsed.map(val => val === 0 ? null : val);

        const total = {
            label: "Total",
            rows: [
                { label: "Estimated", values: grandTotalValues },
                { label: "Procured", values: finalProcuredTotals },
                { label: "Used", values: finalUsedTotals }
            ]
        };

        return { materialColumns: cols, sections, total, columns: apiData.columns };
    };

    // ── Procurement: Fetch material summary from API ───────────────────────────
    const fetchMaterialSummary = async () => {
        setProcurementLoading(true);
        setProcurementError(null);
        try {
            const response = await postApi(estimationRunMaterialSummaryJson, {
                project_id: projectId
            }, true);
            if (response.data && response.data.status === "success") {
                const transformed = transformMaterialSummaryToProcurement(response.data);
                if (transformed) {
                    setMaterialColumns(transformed.materialColumns);
                    setProcurementSections(transformed.sections);
                    setProcurementTotal(transformed.total);
                } else {
                    setMaterialColumns([]);
                    setProcurementSections(null);
                    setProcurementTotal(null);
                }
            } else {
                setMaterialColumns([]);
                setProcurementSections(null);
                setProcurementTotal(null);
            }
        } catch (err) {
            console.error("Error fetching material summary:", err);
            setProcurementError(err?.response?.data?.detail || "Failed to load procurement data");
            setMaterialColumns([]);
            setProcurementSections(null);
            setProcurementTotal(null);
        } finally {
            setProcurementLoading(false);
        }
    };

    // ── Labour: Transform API response to table format ────────────────────────
    const transformLabourSummaryToTable = (apiData) => {
        if (!apiData || !apiData.floors || apiData.floors.length === 0) return null;

        const cols = apiData.columns.map(c => c.key);
        const nullRow = () => new Array(cols.length).fill(null);

        // Build allocatedData object from API response
        const allocatedData = {};

        // Build sections from the API's floors
        const sections = apiData.floors.map(floor => {
            const stages = (floor.modules || []).map(mod => ({
                name: mod.label,
                items: (mod.items || []).map(item => {
                    // Extract allocated data from the API response
                    if (item.allocated) {
                        const allocKey = `${floor.floor_label}|${mod.label}|${item.label}`;
                        // Convert allocated object to array matching column order
                        allocatedData[allocKey] = cols.map(col => item.allocated[col] ?? null);
                    }
                    
                    return {
                        label: item.label,
                        work_qty: item.work_qty,
                        work_unit: item.work_unit,
                        // Map item.labours (estimated - keyed by labour type) to column positions
                        estimated: cols.map(col => item.labours?.[col] ?? null)
                    };
                }),
                totals: mod.totals || {}
            }));

            return {
                sectionTitle: floor.floor_label.toUpperCase(),
                floorLabel: floor.floor_label,
                stages
            };
        });

        // Build grand total rows
        const grandTotalEstimated = nullRow();
        if (apiData.grand_total) {
            cols.forEach((col, i) => {
                if (apiData.grand_total[col] !== undefined) {
                    grandTotalEstimated[i] = apiData.grand_total[col];
                }
            });
        }

        // Grand total Allocated = sum of all item allocations from allocatedData
        const grandTotalAllocated = new Array(cols.length).fill(0);
        Object.values(allocatedData).forEach(values => {
            values.forEach((val, idx) => {
                if (val != null && !isNaN(val)) {
                    grandTotalAllocated[idx] += Number(val);
                }
            });
        });

        const finalAllocatedTotals = grandTotalAllocated.map(val => val === 0 ? null : val);

        const total = {
            label: "Total",
            rows: [
                { label: "Estimated", values: grandTotalEstimated },
                { label: "Allocated", values: finalAllocatedTotals }
            ]
        };

        return { labourColumns: cols, sections, total, allocatedData };
    };

    // ── Labour: Fetch labour summary from API ─────────────────────────────────
    const fetchLabourSummary = async () => {
        if (labourLoading) return;
        setLabourLoading(true);
        setLabourError(null);
        try {
            const response = await postApi(estimationRunLabourSummaryJson, {
                project_id: projectId
            }, true);
            if (response.data && response.data.status === "success") {
                // console.log('=== Labour API Response ===');
                // console.log('Raw API data:', response.data);
                // console.log('API columns:', response.data.columns);
                
                const transformed = transformLabourSummaryToTable(response.data);
                if (transformed) {
                    // console.log('Transformed labour columns:', transformed.labourColumns);
                    // console.log('Available labour state:', availableLabour);
                    
                    setLabourColumns(transformed.labourColumns);
                    setLabourSections(transformed.sections);
                    setLabourTotal(transformed.total);
                    // Load saved allocated data from API response
                    setLabourAllocated(transformed.allocatedData || {});
                } else {
                    setLabourColumns([]);
                    setLabourSections(null);
                    setLabourTotal(null);
                    setLabourAllocated({});
                }
            } else {
                setLabourColumns([]);
                setLabourSections(null);
                setLabourTotal(null);
                setLabourAllocated({});
            }
        } catch (err) {
            console.error("Error fetching labour summary:", err);
            setLabourError(err?.response?.data?.detail || "Failed to load labour data");
            setLabourColumns([]);
            setLabourSections(null);
            setLabourTotal(null);
            setLabourAllocated({});
        } finally {
            setLabourLoading(false);
        }
    };

    // ── Labour: Fetch available labour from project detail ────────────────────
    const fetchAvailableLabour = async () => {
        try {
            const response = await getApi(`${projectDetail}/${projectId}`, true);
            if (response.data) {
                // Check if new per-type fields exist, otherwise fall back to legacy total_available_labour
                const hasNewFields = response.data.available_labour_helper !== undefined ||
                                   response.data.available_labour_mason !== undefined ||
                                   response.data.available_labour_mixer !== undefined ||
                                   response.data.available_labour_operator !== undefined ||
                                   response.data.available_labour_bar_bender !== undefined ||
                                   response.data.available_labour_carpenter !== undefined;
                
                let labourData;
                if (hasNewFields) {
                    // Combine mixer + operator into single mixerOperator field for UI
                    const mixer = Number(response.data.available_labour_mixer || 0);
                    const operator = Number(response.data.available_labour_operator || 0);
                    
                    labourData = {
                        helper: Number(response.data.available_labour_helper || 0),
                        mason: Number(response.data.available_labour_mason || 0),
                        mixerOperator: mixer + operator,
                        barBender: Number(response.data.available_labour_bar_bender || 0),
                        carpenter: Number(response.data.available_labour_carpenter || 0)
                    };
                } else {
                    // Legacy fallback: distribute total_available_labour evenly or set to 0
                    const total = Number(response.data.total_available_labour || 0);
                    labourData = {
                        helper: 0,
                        mason: 0,
                        mixerOperator: 0,
                        barBender: 0,
                        carpenter: 0
                    };
                    // Optionally store total for reference
                    labourData._legacyTotal = total;
                }
                
                setAvailableLabour(labourData);
                setAvailableLabourEdit(labourData);
            }
        } catch (err) {
            console.error("Error fetching available labour:", err);
        }
    };

    // ── Labour: Available Labour Edit/Save/Cancel handler functions ───────────
    const handleEditAvailableLabour = () => {
        setIsEditingAvailableLabour(true);
        setAvailableLabourEdit({ ...availableLabour });
    };

    const handleCancelAvailableLabour = () => {
        setIsEditingAvailableLabour(false);
        setAvailableLabourEdit({ ...availableLabour });
    };

    const handleSaveAvailableLabour = async () => {
        if (!availableLabourEdit) return;
        setAvailableLabourSaving(true);
        try {
            // Send mixerOperator value to both mixer and operator fields in backend
            const mixerOperatorValue = Number(availableLabourEdit.mixerOperator || 0);
            
            await putApi(`${projectDetail}/${projectId}`, {
                available_labour_helper: Number(availableLabourEdit.helper || 0),
                available_labour_mason: Number(availableLabourEdit.mason || 0),
                available_labour_mixer: mixerOperatorValue,
                available_labour_operator: mixerOperatorValue,
                available_labour_bar_bender: Number(availableLabourEdit.barBender || 0),
                available_labour_carpenter: Number(availableLabourEdit.carpenter || 0)
            }, true);
            setAvailableLabour({ ...availableLabourEdit });
            setLabourSaveSuccess("Available labour saved successfully!");
            setTimeout(() => setLabourSaveSuccess(null), 3000);
            setIsEditingAvailableLabour(false);
        } catch (err) {
            console.error("Error saving available labour:", err);
            alert(err?.response?.data?.detail || "Failed to save available labour");
        } finally {
            setAvailableLabourSaving(false);
        }
    };

    // ── Labour: Edit/Save/Cancel handler functions ──────────────────────────
    const handleEditLabour = () => {
        setIsEditingLabour(true);
        setLabourEditData({ ...labourAllocated });
    };

    const handleCancelEditLabour = () => {
        setIsEditingLabour(false);
        setLabourEditData({});
    };

    const handleSaveLabour = async () => {
        setLabourSaving(true);
        try {
            // Build labour_allocation_data array from labourEditData
            // API expects: floor_label, module_name, item_label, labour_type, allocated_quantity
            const labour_allocation_data = [];
            
            Object.entries(labourEditData).forEach(([key, values]) => {
                // key format: "floorLabel|moduleName|itemLabel"
                const [floorLabel, moduleName, itemLabel] = key.split('|');
                
                // For each labour type column
                labourColumns.forEach((labourType, idx) => {
                    const allocatedQty = values?.[idx];
                    
                    // Only add if value exists
                    if (allocatedQty != null && allocatedQty !== '') {
                        labour_allocation_data.push({
                            floor_label: floorLabel,
                            module_name: moduleName,
                            item_label: itemLabel,
                            labour_type: labourType,
                            allocated_quantity: Number(allocatedQty)
                        });
                    }
                });
            });
            
            const response = await putApi(saveLabourAllocation, {
                project_id: projectId,
                labour_allocation_data: labour_allocation_data
            }, true);
            
            if (response.data && response.data.status === "success") {
                setLabourSaveSuccess("Labour allocation saved successfully!");
                setTimeout(() => setLabourSaveSuccess(null), 3000);
                setIsEditingLabour(false);
                setLabourEditData({});
                // Update local state with saved data
                setLabourAllocated({ ...labourEditData });
                // Refresh labour data to show updated values from backend
                await fetchLabourSummary();
            }
        } catch (err) {
            console.error("Error saving labour allocation:", err);
            alert(err?.response?.data?.detail || "Failed to save labour allocation data");
        } finally {
            setLabourSaving(false);
        }
    };

    const handleLabourCellChange = (floorLabel, moduleName, itemLabel, colIdx, value) => {
        const key = `${floorLabel}|${moduleName}|${itemLabel}`;
        const labourType = labourColumns[colIdx];
        
        // Validate against available labour for this type
        let numValue = value === '' ? null : Number(value);
        if (numValue !== null && availableLabourEdit && labourType) {
            // Use normalizeLabourType to convert column name to state key
            const normalizedKey = normalizeLabourType(labourType);
            const availableForType = getAvailableLabourCount(normalizedKey, availableLabourEdit);
            
            // Calculate total allocated for this labour type across all items
            let totalAllocatedForType = 0;
            Object.entries(labourEditData).forEach(([k, values]) => {
                const val = values[colIdx];
                if (val != null && val !== '' && !isNaN(Number(val))) {
                    totalAllocatedForType += Number(val);
                }
            });
            
            // Check if new value would exceed available
            const currentValue = labourEditData[key]?.[colIdx] || 0;
            const newTotal = totalAllocatedForType - (currentValue || 0) + (numValue || 0);
            
            if (newTotal > availableForType) {
                const msg = `Cannot allocate more ${labourType} than available (${availableForType}). Current allocation would exceed by ${newTotal - availableForType}.`;
                setAllocationErrorMessage(msg);
                setShowAllocationErrorModal(true);
                return; // Don't update the value
            }
        }
        
        setLabourEditData(prev => {
            const newValues = [...(prev[key] || [])];
            newValues[colIdx] = numValue;
            return { ...prev, [key]: newValues };
        });
    };

    // ── Procurement: Edit/Save/Cancel handler functions ────────────────────────
    const handleEditProcurement = () => {
        setIsEditingProcurement(true);
        // Initialize edit data from current sections
        const editData = {};
        procurementSections.forEach(section => {
            section.stages.forEach(stage => {
                stage.rows.forEach(row => {
                    if (row.label !== "Estimated") {
                        const key = `${section.floorLabel}|${stage.name}|${row.label}`;
                        editData[key] = [...row.values];
                    }
                });
            });
        });
        setProcurementEditData(editData);
    };

    const handleCancelEdit = () => {
        setIsEditingProcurement(false);
        setProcurementEditData({});
    };

    const handleSaveProcurement = async () => {
        setProcurementSaving(true);
        try {
            // Build procurement_data array from editData
            // API expects one item per material with fields: floor_label, module_name, material_key, procured_quantity, used_quantity
            const procurement_data = [];
            
            procurementSections.forEach(section => {
                section.stages.forEach(stage => {
                    const procuredKey = `${section.floorLabel}|${stage.name}|Procured`;
                    const usedKey = `${section.floorLabel}|${stage.name}|Used`;
                    
                    const procuredValues = procurementEditData[procuredKey] || [];
                    const usedValues = procurementEditData[usedKey] || [];
                    
                    // For each material column, create a separate entry if it has procured or used values
                    materialColumns.forEach((colLabel, idx) => {
                        // Extract material key by removing only the LAST unit portion in parentheses
                        // E.g., "Coarse Aggregate (40 mm) (cum)" → "Coarse Aggregate (40 mm)"
                        // E.g., "Cement (bags)" → "Cement"
                        const materialKey = colLabel.replace(/\s*\([^)]*\)$/, '');
                        const procuredQty = procuredValues[idx];
                        const usedQty = usedValues[idx];
                        
                        // Only add if at least one value exists
                        if ((procuredQty != null && procuredQty !== '') || 
                            (usedQty != null && usedQty !== '')) {
                            procurement_data.push({
                                floor_label: section.floorLabel,
                                module_name: stage.name,
                                material_key: materialKey,
                                procured_quantity: procuredQty != null && procuredQty !== '' ? Number(procuredQty) : null,
                                used_quantity: usedQty != null && usedQty !== '' ? Number(usedQty) : null
                            });
                        }
                    });
                });
            });
            
            const response = await putApi(saveMaterialProcurement, {
                project_id: projectId,
                procurement_data: procurement_data
            }, true);
            
            if (response.data && response.data.status === "success") {
                setProcurementSaveSuccess("Procurement data saved successfully!");
                setTimeout(() => setProcurementSaveSuccess(null), 3000);
                setIsEditingProcurement(false);
                // Refresh data to show updated values
                await fetchMaterialSummary();
            }
        } catch (err) {
            console.error("Error saving procurement data:", err);
            alert(err?.response?.data?.detail || "Failed to save procurement data");
        } finally {
            setProcurementSaving(false);
        }
    };

    const handleProcurementCellChange = (floorLabel, stageName, rowLabel, colIdx, value) => {
        const key = `${floorLabel}|${stageName}|${rowLabel}`;
        setProcurementEditData(prev => {
            const newValues = [...(prev[key] || [])];
            newValues[colIdx] = value === '' ? null : Number(value);
            return { ...prev, [key]: newValues };
        });
    };

    // ── Milestone Tab Functions ──────────────────────────────────────────────────
    
    // Fetch all milestones for the project
    const fetchMilestones = async () => {
        setMilestonesLoading(true);
        setMilestonesError(null);
        try {
            const response = await getApi(projectMilestones(projectId), true);
            if (response.data && response.data.data) {
                setMilestones(response.data.data);
            } else {
                setMilestones([]);
            }
        } catch (err) {
            console.error("Error fetching milestones:", err);
            setMilestonesError(err?.response?.data?.detail || "Failed to load milestones");
            setMilestones([]);
        } finally {
            setMilestonesLoading(false);
        }
    };

    // Get available tasks from CPM data
    const getAvailableTasks = () => {
        if (!cpmData || !cpmData.all_activities) return [];
        return cpmData.all_activities.map((activity, index) => ({
            id: String(activity.activity_id) + '_' + index,
            label: activity.label,
            stage_label: activity.stage_label,
            phase: activity.phase || 'Other',
            originalId: String(activity.activity_id)
        }));
    };

    // Open create milestone modal
    const handleCreateMilestone = () => {
        setEditingMilestone(null);
        setMilestoneForm({
            name: "",
            description: "",
            order: "",
            start_date: "",
            end_date: ""
        });
        setMilestoneFormValidated(false);
        setDateValidationError("");
        setShowMilestoneModal(true);
    };

    // Open edit milestone modal
    const handleEditMilestone = (milestone) => {
        setEditingMilestone(milestone);
        setMilestoneForm({
            name: milestone.name || "",
            description: milestone.description || "",
            order: milestone.order || "",
            start_date: milestone.start_date ? moment(milestone.start_date, "DD MMM YYYY").format("YYYY-MM-DD") : "",
            end_date: milestone.end_date ? moment(milestone.end_date, "DD MMM YYYY").format("YYYY-MM-DD") : ""
        });
        setMilestoneFormValidated(false);
        setDateValidationError("");
        setShowMilestoneModal(true);
    };

    // Save milestone (create or update)
    const handleSaveMilestone = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        // Clear previous date validation error
        setDateValidationError("");
        
        if (form.checkValidity() === false) {
            e.stopPropagation();
            setMilestoneFormValidated(true);
            return;
        }

        // Validate milestone name uniqueness
        if (!validateMilestoneNameUniqueness(milestoneForm.name, milestones, editingMilestone)) {
            alert("Milestone name has already been taken. Please use a different milestone name.");
            return;
        }

        // Validate dates: End Date must be after Start Date
        if (milestoneForm.start_date && milestoneForm.end_date) {
            const startDate = moment(milestoneForm.start_date, "YYYY-MM-DD");
            const endDate = moment(milestoneForm.end_date, "YYYY-MM-DD");
            if (endDate.isBefore(startDate)) {
                setDateValidationError("End Date must be after Start Date.");
                setMilestoneFormValidated(true);
                return;
            }
        }

        setMilestoneSaving(true);
        try {
            const payload = {
                name: milestoneForm.name.trim(),
                description: milestoneForm.description?.trim() || "",
                order: milestoneForm.order ? Number(milestoneForm.order) : undefined,
                start_date: milestoneForm.start_date ? moment(milestoneForm.start_date, "YYYY-MM-DD").format("DD MMM YYYY") : undefined,
                end_date: milestoneForm.end_date ? moment(milestoneForm.end_date, "YYYY-MM-DD").format("DD MMM YYYY") : undefined
            };

            if (editingMilestone) {
                // Update existing milestone
                await putApi(projectMilestoneDetail(projectId, editingMilestone._id), payload, true);
                setMilestoneSuccess("Milestone updated successfully!");
            } else {
                // Create new milestone
                payload.project_id = projectId;
                await postApi(projectMilestones(projectId), payload, true);
                setMilestoneSuccess("Milestone created successfully!");
            }

            setShowMilestoneModal(false);
            await fetchMilestones();
            
            setTimeout(() => setMilestoneSuccess(null), 3000);
        } catch (err) {
            console.error("Error saving milestone:", err);
            
            // Handle duplicate name error from backend
            if (err?.response?.data?.detail?.includes("already been taken")) {
                alert("Milestone name has already been taken. Please use a different milestone name.");
            } else {
                alert(err?.response?.data?.detail || "Failed to save milestone");
            }
        } finally {
            setMilestoneSaving(false);
        }
    };

    // Delete milestone
    const handleDeleteMilestone = (milestoneId) => {
        setMilestoneToDelete(milestoneId);
        setShowDeleteMilestoneModal(true);
    };

    const handleConfirmDeleteMilestone = async () => {
        if (!milestoneToDelete) return;

        try {
            await deleteApi(projectMilestoneDetail(projectId, milestoneToDelete), true);
            setMilestoneSuccess("Milestone deleted successfully!");
            await fetchMilestones();
            setTimeout(() => setMilestoneSuccess(null), 3000);
        } catch (err) {
            console.error("Error deleting milestone:", err);
            alert(err?.response?.data?.detail || "Failed to delete milestone");
        } finally {
            setShowDeleteMilestoneModal(false);
            setMilestoneToDelete(null);
        }
    };

    // Open task linking modal
    const handleOpenTaskLinkModal = (milestone) => {
        setSelectedMilestoneForTasks(milestone);
        const availableTasks = getAvailableTasks();
        
        // Build map of task_id -> milestone name for tasks assigned to OTHER milestones
        const taskToMilestoneMap = {};
        milestones.forEach(m => {
            if (m._id !== milestone._id && m.task_ids) {
                m.task_ids.forEach(id => {
                    taskToMilestoneMap[id] = m.name;
                });
            }
        });
        
        // Add assignedMilestoneName to tasks that are assigned to other milestones
        const tasksWithAssignmentInfo = availableTasks.map(task => ({
            ...task,
            assignedToOtherMilestone: taskToMilestoneMap[task.originalId] || null
        }));
        
        // Map backend task_ids to composite keys used in availableTasks
        const taskIdsMap = {};
        tasksWithAssignmentInfo.forEach(task => {
            if (!taskIdsMap[task.originalId]) {
                taskIdsMap[task.originalId] = task.id;
            }
        });
        
        const selectedCompositeIds = (milestone.task_ids || []).map(id => taskIdsMap[id] || id);
        setSelectedTaskIds(selectedCompositeIds);
        setAvailableTasks(tasksWithAssignmentInfo);
        setTaskSearchQuery("");
        setShowTaskLinkModal(true);
    };

    // Toggle task selection
    const handleToggleTask = (taskId) => {
        // Find the task to check if it's assigned to another milestone
        const task = availableTasks.find(t => t.id === taskId);
        if (task && task.assignedToOtherMilestone) {
            // Task is already assigned to another milestone - don't allow selection
            return;
        }
        
        setSelectedTaskIds(prev => {
            if (prev.includes(taskId)) {
                return prev.filter(id => id !== taskId);
            } else {
                return [...prev, taskId];
            }
        });
    };

    // Save task links
    const handleSaveTaskLinks = async () => {
        if (!selectedMilestoneForTasks) return;

        setTaskLinkingSaving(true);
        try {
            // Extract original activity_ids from composite keys
            const taskIdsToSend = selectedTaskIds.map(id => id.split('_')[0]);
            
            await postApi(milestoneTasks(projectId, selectedMilestoneForTasks._id), {
                task_ids: taskIdsToSend
            }, true);
            
            setMilestoneSuccess(`Successfully linked ${selectedTaskIds.length} tasks to milestone!`);
            setShowTaskLinkModal(false);
            await fetchMilestones();
            setTimeout(() => setMilestoneSuccess(null), 3000);
        } catch (err) {
            console.error("Error linking tasks:", err);
            alert(err?.response?.data?.detail || "Failed to link tasks to milestone");
        } finally {
            setTaskLinkingSaving(false);
        }
    };

    // Unlink single task from milestone
    const handleUnlinkTask = (milestone, taskId) => {
        setTaskToUnlink({ milestone, taskId });
        setShowUnlinkTaskModal(true);
    };

    const handleConfirmUnlinkTask = async () => {
        if (!taskToUnlink) return;
        const { milestone, taskId } = taskToUnlink;

        try {
            await deleteApi(milestoneTaskUnlink(projectId, milestone._id, taskId), true);
            setMilestoneSuccess("Task unlinked successfully!");
            await fetchMilestones();
            setTimeout(() => setMilestoneSuccess(null), 3000);
        } catch (err) {
            console.error("Error unlinking task:", err);
            alert(err?.response?.data?.detail || "Failed to unlink task");
        } finally {
            setShowUnlinkTaskModal(false);
            setTaskToUnlink(null);
        }
    };

    // Get task details by ID
    const getTaskById = (taskId) => {
        if (!cpmData || !cpmData.all_activities) return null;
        // Extract original activity_id from composite key (format: "activity_id_index")
        const originalId = taskId.split('_')[0];
        return cpmData.all_activities.find(activity => String(activity.activity_id) === originalId);
    };

    // ── Cost Analysis Tab State ───────────────────────────────────────────────────
    const [costSummaryRows, setCostSummaryRows] = useState(null);
    const [costSummaryLoading, setCostSummaryLoading] = useState(false);
    const [costSummaryError, setCostSummaryError] = useState(null);
    const [actualCosts, setActualCosts] = useState({});
    
    // ── Cost Analysis Edit Mode State ─────────────────────────────────────────
    const [isEditingCosts, setIsEditingCosts] = useState(false);
    const [costEditData, setCostEditData] = useState({});
    const [costSaving, setCostSaving] = useState(false);
    const [costSaveSuccess, setCostSaveSuccess] = useState(null);

    // ── Cost Analysis: Fetch cost summary from API ──────────────────────────────
    const fetchCostSummary = async () => {
        if (costSummaryLoading) return;
        setCostSummaryLoading(true);
        setCostSummaryError(null);
        try {
            const response = await postApi(estimationRunCostSummaryJson, {
                project_id: projectId
            }, true);
            if (response.data && response.data.status === "success") {
                setCostSummaryRows(response.data.rows || []);
                // Load saved actual costs from module_subtotal rows
                // The backend injects row.actual_cost onto each module_subtotal row
                const savedCosts = {};
                let curFloor = null;
                let curModule = null;
                (response.data.rows || []).forEach(row => {
                    if (row.type === 'floor') {
                        curFloor = row['Floor / Module'];
                        curModule = null;
                    } else if (row.type === 'module') {
                        curModule = row['Floor / Module'];
                    } else if (row.type === 'module_subtotal' && curFloor && curModule && row.actual_cost != null) {
                        savedCosts[`${curFloor}|${curModule}`] = Number(row.actual_cost);
                    }
                });
                setActualCosts(savedCosts);
            } else {
                setCostSummaryRows(null);
                setCostSummaryError("Failed to load cost summary data");
            }
        } catch (err) {
            console.error("Error fetching cost summary:", err);
            setCostSummaryError(err?.response?.data?.detail || "Failed to load cost summary data");
            setCostSummaryRows(null);
        } finally {
            setCostSummaryLoading(false);
        }
    };

    // ── Cost Analysis: Edit/Save/Cancel handler functions ──────────────────────
    const handleEditCosts = () => {
        setIsEditingCosts(true);
        // Initialize edit data from existing actual costs state
        setCostEditData({ ...actualCosts });
    };

    const handleCancelEditCosts = () => {
        setIsEditingCosts(false);
        setCostEditData({});
    };

    const handleSaveCosts = async () => {
        setCostSaving(true);
        try {
            // Build actual_cost_data array from costEditData
            // API expects one item per module with fields: floor_label, module_name, actual_cost
            const actual_cost_data = [];
            
            Object.entries(costEditData).forEach(([key, value]) => {
                const [floorLabel, ...rest] = key.split('|');
                const moduleName = rest.join('|');
                const actualCost = (value !== '' && value != null && !isNaN(Number(value)))
                    ? Number(value)
                    : null;
                actual_cost_data.push({
                    floor_label: floorLabel,
                    module_name: moduleName,
                    actual_cost: actualCost
                });
            });
            
            const response = await putApi(saveActualCost, {
                project_id: projectId,
                actual_cost_data: actual_cost_data
            }, true);
            
            if (response.data && response.data.status === "success") {
                setCostSaveSuccess("Actual costs saved successfully!");
                setTimeout(() => setCostSaveSuccess(null), 3000);
                setIsEditingCosts(false);
                setCostEditData({});
                // Refresh data to show updated values
                await fetchCostSummary();
            }
        } catch (err) {
            console.error("Error saving actual costs:", err);
            alert(err?.response?.data?.detail || "Failed to save actual costs");
        } finally {
            setCostSaving(false);
        }
    };

    const handleCostCellChange = (floorLabel, moduleName, value) => {
        const key = `${floorLabel}|${moduleName}`;
        setCostEditData(prev => ({
            ...prev,
            [key]: value === '' ? '' : Number(value),
        }));
    };

    // ── Assessment Tab State ─────────────────────────────────────────────────────
    const [assessments, setAssessments] = useState([
        { id: 1, stage: "Substructure", name: "Foundation Inspection", assignedTo: "John Doe", scheduledDate: "2026-07-15", completedDate: "2026-07-16", result: assessmentResults?.data?.[0] },
        { id: 2, stage: "Superstructure", name: "Steel Quality Check", assignedTo: "Jane Smith", scheduledDate: "2026-07-10", completedDate: null, result: assessmentResults?.data?.[0] },
        { id: 3, stage: "Masonry", name: "Brick Work Review", assignedTo: "Sam Wilson", scheduledDate: "2026-08-01", completedDate: null, result: assessmentResults?.data?.[0] }
    ]);
    const [showAssessmentModal, setShowAssessmentModal] = useState(false);
    const [editingAssessment, setEditingAssessment] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [viewResult, setViewResult] = useState(null);
    const [assessmentFilterStage, setAssessmentFilterStage] = useState("all");
    const [assessmentForm, setAssessmentForm] = useState({
        name: "", assignedTo: "", stage: "", scheduledDate: "", completedDate: ""
    });
    const [assessmentFormValidated, setAssessmentFormValidated] = useState(false);

    // ── Actual Start/Finish State ───────────────────────────────────────────────
    const [showActualStartModal, setShowActualStartModal] = useState(false);
    const [showActualFinishModal, setShowActualFinishModal] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [actualStartDate, setActualStartDate] = useState("");
    const [actualFinishDate, setActualFinishDate] = useState("");
    const [actualStartValidated, setActualStartValidated] = useState(false);
    const [actualFinishValidated, setActualFinishValidated] = useState(false);
    const [savingActualDate, setSavingActualDate] = useState(false);    
    const [showScrollTop, setShowScrollTop] = useState(false);

    // ── Scroll-to-top visibility ────────────────────────────────────────────────
    useEffect(() => {
        const contentBox = document.querySelector('.content-box');
        const handleScroll = () => {
            if (contentBox) {
                setShowScrollTop(contentBox.scrollTop > 300);
            }
        };
        if (contentBox) {
            contentBox.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (contentBox) {
                contentBox.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    // ── Assessment Helper Functions ─────────────────────────────────────────────
    const deriveAssessmentStatus = (a) => {
        if (a.completedDate) return "Completed";
        if (moment(a.scheduledDate).isBefore(moment().startOf("day"))) return "Overdue";
        return "Pending";
    };

    const handleAssessmentFormSubmit = (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (!form.checkValidity()) {
            setAssessmentFormValidated(true);
            return;
        }
        setAssessmentFormValidated(false);

        const formData = {
            name: assessmentForm.name.trim(),
            assignedTo: assessmentForm.assignedTo.trim(),
            stage: assessmentForm.stage,
            scheduledDate: assessmentForm.scheduledDate,
            completedDate: assessmentForm.completedDate || null
        };

        if (editingAssessment) {
            // Update existing
            setAssessments(prev =>
                prev.map(a => a.id === editingAssessment.id ? { ...a, ...formData } : a)
            );
        } else {
            // Add new
            const newId = assessments.length > 0 ? Math.max(...assessments.map(a => a.id)) + 1 : 1;
            setAssessments(prev => [...prev, { id: newId, ...formData }]);
        }
        setShowAssessmentModal(false);
        setEditingAssessment(null);
    };

    const handleDeleteAssessment = () => {
        if (deleteConfirmId !== null) {
            setAssessments(prev => prev.filter(a => a.id !== deleteConfirmId));
            setDeleteConfirmId(null);
        }
    };

    const getFilteredAssessments = () => {
        if (assessmentFilterStage === "all") return assessments;
        return assessments.filter(a => a.stage === assessmentFilterStage);
    };

    useEffect(() => {
        if (projectId) {
            fetchProjectAndCPM();
            fetchMaterialSummary();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => {
        if (cpmData) {
            const stages = getUniqueStages();
            if (stages.length > 0) {
                // Preserve the user's selected stage when CPM data refreshes
                // (e.g., after saving Actual Start/Finish). Only auto-select the
                // first stage on initial load, or if the selected stage vanished.
                if (filterStage === "all" || !stages.includes(filterStage)) {
                    setFilterStage(stages[0]);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cpmData, filterStage]);

    // Fetch cost summary when the Cost Analysis tab becomes active
    useEffect(() => {
        if (activeTab === "cost-analysis" && !costSummaryRows && !costSummaryLoading) {
            fetchCostSummary();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Fetch labour summary when the Labour tab becomes active
    useEffect(() => {
        if (activeTab === "labour" && !labourSections && !labourLoading) {
            fetchLabourSummary();
            fetchAvailableLabour();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Fetch milestones on component mount (needed for Tasks tab to show milestone names)
    useEffect(() => {
        if (!milestonesLoading && milestones.length === 0) {
            fetchMilestones();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize Bootstrap tooltips when task link modal opens
    useEffect(() => {
        if (showTaskLinkModal) {
            // Wait for modal to render, then initialize tooltips
            const timer = setTimeout(() => {
                const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
                tooltipTriggerList.map(function (tooltipTriggerEl) {
                    return new bootstrap.Tooltip(tooltipTriggerEl);
                });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showTaskLinkModal]);

    const fetchProjectAndCPM = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Fetch project details
            const projectResponse = await getApi(`${projectDetail}/${projectId}`, true);
            setProjectData(projectResponse.data);
            
            // Check if CPM data already exists from previous run
            // If not, we might need to run CPM first
            // For now, let's try to fetch existing CPM data
            await fetchCPMData();
            
        } catch (err) {
            console.error("Error fetching data:", err);
            setError(err?.response?.data?.detail || "Failed to load CPM data");
            setLoading(false);
        }
    };

    const fetchCPMData = async () => {
        try {
            // Try GET to retrieve previously persisted CPM result
            const url = getCpmResult(projectId);
            const response = await getApi(url, true);
            if (response.data) {
                setCpmData(response.data);
                // Restore persisted work-started date so the button reflects saved state
                setWorkStarted(!!response.data.work_start_date);
                setWorkStartDate(response.data.work_start_date || null);
            }
            setLoading(false);
        } catch (err) {
            console.info("No persisted CPM result found — user can run CPM manually:", err?.response?.status);
            // 404 or any error means no persisted data; show actionable error
            setError("CPM data not available. Please run CPM analysis first.");
            setLoading(false);
        }
    };

    const handleRunCPM = () => {
        // Open the date-picker modal to run CPM analysis with start/end dates
        const startDateStr = cpmData?.start_date || "";
        const endDateStr = cpmData?.end_date || "";
        setNewStartDate(startDateStr ? moment(startDateStr, "DD MMM YYYY").format("YYYY-MM-DD") : "");
        setNewEndDate(endDateStr ? moment(endDateStr, "DD MMM YYYY").format("YYYY-MM-DD") : "");
        setValidated(false);
        setShowResetModal(true);
    };

    const handleStartWork = async () => {
        if (!workStarted) {
            const now = moment().format('DD MMM YYYY');
            setWorkStarted(true);
            setWorkStartDate(now);
            try {
                await putApi(updateProjectWorkStart, {
                    project_id: projectId,
                    work_start_date: now
                }, true);
            } catch (err) {
                console.error("Error saving work start date:", err);
                alert(err?.response?.data?.detail || "Failed to save work start date");
            }
        }
    };

    const handleOpenResetModal = () => {
        // Parse persisted dates (backend format "DD MMM YYYY") back to YYYY-MM-DD for the date inputs
        const startDateStr = cpmData?.start_date || "";
        const endDateStr = cpmData?.end_date || "";
        setNewStartDate(startDateStr ? moment(startDateStr, "DD MMM YYYY").format("YYYY-MM-DD") : "");
        setNewEndDate(endDateStr ? moment(endDateStr, "DD MMM YYYY").format("YYYY-MM-DD") : "");
        setValidated(false);
        setModalError(null);
        setShowResetModal(true);
    };

    const handleRunCPMWithDates = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (!form.checkValidity()) {
            setValidated(true);
            return;
        }
        
        setValidated(false);
        setModalError(null);
        setLoading(true);
        setError(null);
        
        try {
            const response = await postApi(runCpm, {
                project_id: projectId,
                start_date: newStartDate,
                target_end_date: newEndDate
            }, true);
            
            if (response.data) {
                setCpmData(response.data);
            }
            setLoading(false);
            setShowResetModal(false);
        } catch (err) {
            console.error("Error running CPM with dates:", err);
            setModalError(err?.response?.data?.detail || "Failed to run CPM analysis");
            setLoading(false);
        }
    };

    const downloadExcel = async () => {
        if (cpmData?.excel_url) {
            try {
                const projectName = (projectData?.name || "Project").replace(/\s+/g, "_");
                // Build a blob promise so the Save As dialog opens immediately (within
                // the user's click activation) while the file is fetched in the background.
                const blobPromise = fetch(cpmData.excel_url)
                    .then(res => {
                        if (!res.ok) throw new Error(`HTTP ${res.status} while fetching Excel`);
                        return res.blob();
                    });

                const saved = await saveFileWithPrompt(blobPromise, `${projectName}_CPM_Results.xlsx`);

                // Only show success when the file was actually saved/initiated;
                // cancelling the Save As dialog resolves false and shows nothing.
                if (saved) {
                    setDownloadSuccess("CPM Excel downloaded successfully!");
                    setTimeout(() => setDownloadSuccess(null), 3000);
                }
            } catch (error) {
                console.error("Error downloading CPM Excel:", error);
                alert("Failed to download CPM Excel. Please try again.");
            }
        }
    };

    const downloadAllImages = async () => {
        if (!cpmData) return;

        const projectName = (projectData?.name || "Project").replace(/\s+/g, "_");
        
        // Create a promise that builds the ZIP blob
        // This allows saveFileWithPrompt to open the picker immediately
        const zipBlobPromise = (async () => {
            const zip = new JSZip();
            const fetchPromises = [];

            // Add mother diagram
            if (cpmData.mother_diagram) {
                const promise = fetch(cpmData.mother_diagram)
                    .then(res => {
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        return res.blob();
                    })
                    .then(blob => {
                        zip.file("cpm_mother_diagram.png", blob);
                    })
                    .catch(err => console.error("Failed to download mother diagram:", err));
                fetchPromises.push(promise);
            }

            // Add stage diagrams
            if (cpmData.stage_diagrams) {
                Object.entries(cpmData.stage_diagrams).forEach(([label, url]) => {
                    const safeName = `cpm_${label.replace(/\s+/g, "_")}.png`;
                    const promise = fetch(url)
                        .then(res => {
                            if (!res.ok) throw new Error(`HTTP ${res.status}`);
                            return res.blob();
                        })
                        .then(blob => {
                            zip.file(safeName, blob);
                        })
                        .catch(err => console.error(`Failed to download stage diagram "${label}":`, err));
                    fetchPromises.push(promise);
                });
            }

            await Promise.all(fetchPromises);
            return zip.generateAsync({ type: "blob" });
        })();

        try {
            const saved = await saveFileWithPrompt(zipBlobPromise, `CPM_Diagrams_${projectName}.zip`);

            // Only show success when the file was actually saved/initiated;
            // cancelling the Save As dialog resolves false and shows nothing.
            if (saved) {
                setDownloadSuccess("CPM diagrams downloaded successfully!");
                setTimeout(() => setDownloadSuccess(null), 3000);
            }
        } catch (err) {
            console.error("Failed to generate ZIP:", err);
            alert("Failed to download CPM diagrams. Please try again.");
        }
    };

    // Get unique stages for filtering
    const getUniqueStages = () => {
        if (!cpmData?.all_activities) return [];
        const stages = [...new Set(cpmData.all_activities.map(act => act.stage_label || act.phase))];
        return stages.filter(Boolean);
    };

    // Filter activities based on selected stage
    const getFilteredActivities = () => {
        if (!cpmData?.all_activities) return [];
        return cpmData.all_activities.filter(act => 
            (act.stage_label === filterStage || act.phase === filterStage)
        );
    };

    // Build mapping: activity_id -> milestone name
    const activityIdToMilestoneMap = useMemo(() => {
        const map = {};
        milestones.forEach(milestone => {
            (milestone.task_ids || []).forEach(taskId => {
                map[String(taskId)] = milestone.name;
            });
        });
        return map;
    }, [milestones]);

    // ── Overall progress summary (derived from activities' actual dates) ──
    const progressSummary = useMemo(() => {
        if (!cpmData?.all_activities || cpmData.all_activities.length === 0) return null;
        const today = moment().startOf("day");
        let totalProgress = 0;
        cpmData.all_activities.forEach((act) => {
            let progress = 0;
            if (act.actual_finish) {
                progress = 100;
            } else if (act.actual_start) {
                const startDate = moment(act.actual_start, "DD MMM YYYY");
                const duration = act.duration || 1;
                const elapsed = today.diff(startDate, "days");
                progress = Math.max(0, Math.min(Math.round((elapsed / duration) * 100), 99));
            }
            totalProgress += progress;
        });
        return {
            overallProgress: Math.round(totalProgress / cpmData.all_activities.length),
        };
    }, [cpmData]);

    // ── Per-stage progress bars (derived from activities' actual dates) ────
    const stageProgressList = useMemo(() => {
        if (!cpmData?.all_activities || cpmData.all_activities.length === 0) return [];
        const today = moment().startOf("day");
        const stageMap = {};

        cpmData.all_activities.forEach((act) => {
            const stageName = act.stage_label || act.phase || "Unknown";
            if (!stageMap[stageName]) {
                stageMap[stageName] = { activities: [] };
            }
            stageMap[stageName].activities.push(act);
        });

        return Object.entries(stageMap).map(([stageName, { activities }]) => {
            let totalProgress = 0;
            activities.forEach((act) => {
                let progress = 0;
                if (act.actual_finish) {
                    progress = 100;
                } else if (act.actual_start) {
                    const startDate = moment(act.actual_start, "DD MMM YYYY");
                    const duration = act.duration || 1;
                    const elapsed = today.diff(startDate, "days");
                    progress = Math.max(0, Math.min(Math.round((elapsed / duration) * 100), 99));
                }
                totalProgress += progress;
            });
            return {
                stageName,
                count: activities.length,
                progress: Math.round(totalProgress / activities.length),
            };
        });
    }, [cpmData]);

    // ── Get status for each stage based on its activities' actual dates ──
    // Returns: { stageName: "completed" | "in-progress" | "not-started" }
    const getStageStatuses = () => {
        if (!cpmData?.all_activities) return {};
        const stageStatuses = {};

        stages.forEach(stage => {
            const stageActivities = cpmData.all_activities.filter(act => 
                (act.stage_label === stage || act.phase === stage)
            );
            if (stageActivities.length === 0) {
                stageStatuses[stage] = "not-started";
                return;
            }

            // If any activity in this stage is critical (zero float / on critical path), mark the stage as critical
            const hasCritical = stageActivities.some(act => act.critical);
            if (hasCritical) {
                stageStatuses[stage] = "critical";
                return;
            }

            let allCompleted = true;
            let anyStarted = false;

            stageActivities.forEach(act => {
                if (act.actual_finish) {
                    // completed — mark the stage as started
                    anyStarted = true;
                } else if (act.actual_start) {
                    // in-progress — neither all completed nor all not started
                    allCompleted = false;
                    anyStarted = true;
                } else {
                    // not started — leave allCompleted false
                    allCompleted = false;
                }
            });

            if (allCompleted) {
                stageStatuses[stage] = "completed";
            } else if (!anyStarted) {
                stageStatuses[stage] = "not-started";
            } else {
                stageStatuses[stage] = "in-progress";
            }
        });

        return stageStatuses;
    };

    // ── Actual Start/Finish Handler Functions ──────────────────────────────────
    const handleOpenActualStartModal = (activity) => {
        setSelectedActivity(activity);
        setActualStartDate(activity.actual_start ? moment(activity.actual_start, "DD MMM YYYY").format("YYYY-MM-DD") : "");
        setActualStartValidated(false);
        setShowActualStartModal(true);
    };

    const handleOpenActualFinishModal = (activity) => {
        setSelectedActivity(activity);
        setActualFinishDate(activity.actual_finish ? moment(activity.actual_finish, "DD MMM YYYY").format("YYYY-MM-DD") : "");
        setActualStartValidated(false);
        setActualFinishValidated(false);
        setShowActualFinishModal(true);
    };

    const handleSaveActualStart = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (!form.checkValidity()) {
            setActualStartValidated(true);
            return;
        }

        // NEW VALIDATION: If finish date exists, start must be <= finish
        if (selectedActivity.actual_finish) {
            const newStartDate = moment(actualStartDate, "YYYY-MM-DD");
            const existingFinishDate = moment(selectedActivity.actual_finish, "DD MMM YYYY");
            
            if (newStartDate.isAfter(existingFinishDate)) {
                alert(`Actual Start Date cannot be later than the existing Actual Finish Date (${selectedActivity.actual_finish}). Please update the finish date first.`);
                return;
            }
        }

        setSavingActualDate(true);
        try {
            const formattedDate = moment(actualStartDate, "YYYY-MM-DD").format("DD MMM YYYY");
            const response = await putApi(updateActivityActualStart, {
                project_id: projectId,
                activity_id: String(selectedActivity.id),
                stage_label: selectedActivity.stage_label || selectedActivity.phase,
                actual_start: formattedDate
            }, true);

            if (response.data && response.data.status === "success") {
                // Refresh CPM data to show updated date
                await fetchCPMData();
                setShowActualStartModal(false);
                setSelectedActivity(null);
                setDownloadSuccess("Actual start date saved successfully!");
                setTimeout(() => setDownloadSuccess(null), 3000);
            }
        } catch (err) {
            console.error("Error saving actual start date:", err);
            alert(err?.response?.data?.detail || "Failed to save actual start date");
        } finally {
            setSavingActualDate(false);
        }
    };

    const handleSaveActualFinish = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (!form.checkValidity()) {
            setActualFinishValidated(true);
            return;
        }

        // Validation: Finish date must be >= Start date
        const actualStartDateValue = selectedActivity.actual_start;
        if (!actualStartDateValue) {
            alert("Please set the Actual Start Date first before setting the Actual Finish Date.");
            return;
        }

        const startDate = moment(actualStartDateValue, "DD MMM YYYY");
        const finishDate = moment(actualFinishDate, "YYYY-MM-DD");
        
        if (finishDate.isBefore(startDate)) {
            alert("Actual Finish Date must be greater than or equal to Actual Start Date.");
            return;
        }

        setSavingActualDate(true);
        try {
            const formattedFinishDate = moment(actualFinishDate, "YYYY-MM-DD").format("DD MMM YYYY");
            const response = await putApi(updateActivityActualFinish, {
                project_id: projectId,
                activity_id: String(selectedActivity.id),
                stage_label: selectedActivity.stage_label || selectedActivity.phase,
                actual_start: actualStartDateValue,
                actual_finish: formattedFinishDate
            }, true);

            if (response.data && response.data.status === "success") {
                // Refresh CPM data to show updated date
                await fetchCPMData();
                setShowActualFinishModal(false);
                setSelectedActivity(null);
                setDownloadSuccess("Actual finish date saved successfully!");
                setTimeout(() => setDownloadSuccess(null), 3000);
            }
        } catch (err) {
            console.error("Error saving actual finish date:", err);
            alert(err?.response?.data?.detail || "Failed to save actual finish date");
        } finally {
            setSavingActualDate(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center py-5 main-content">
                <Spinner animation="border" variant="purple" />
                <span className="ms-3">Loading CPM data...</span>
            </div>
        );
    }

    if (error && !cpmData) {
        return (
            <div className="project-cpm-container main-content animate-fade-in">
                <div className="cpm-empty-state">
                    <div className="row align-items-center g-0 cpm-empty-state-row">
                        <div className="col-12 col-md-4">
                            <div className="cpm-illustration">
                        <svg viewBox="0 0 390 330" fill="none" aria-hidden="true" focusable="false">
                            <path d="M45 228c-18-66 23-131 91-137 40-4 71-36 119-13 52 25 75 86 50 138-24 50-85 66-139 69-58 3-108-15-121-57Z" fill="#f0f2ff"/>
                            <path d="M73 241c9-62 42-100 92-119 54-21 112 7 119 63 7 53-31 95-87 105-55 10-132 4-124-49Z" fill="#f5f6ff"/>
                            <g transform="rotate(-11 193 176)">
                                <rect x="118" y="82" width="154" height="178" rx="17" fill="#fff" stroke="#e0e5fb" strokeWidth="3"/>
                                <rect x="148" y="135" width="20" height="57" rx="7" fill="#b9c3fb"/>
                                <rect x="181" y="116" width="20" height="76" rx="7" fill="#aebafa"/>
                                <rect x="214" y="97" width="20" height="95" rx="7" fill="#a3b1f8"/>
                                <rect x="147" y="210" width="88" height="9" rx="4.5" fill="#aab6f4"/>
                                <rect x="147" y="229" width="64" height="9" rx="4.5" fill="#c1c9f7"/>
                            </g>
                            <circle cx="268" cy="220" r="47" fill="#ff465b"/>
                            <path d="M249 201l38 38M287 201l-38 38" stroke="#fff" strokeWidth="10" strokeLinecap="round"/>
                            <path d="M83 126l-14-13M87 150l-19 1M89 173l-12 16M298 111l14-20M318 135l19-4M313 154l15 8" stroke="#8d9cf0" strokeWidth="7" strokeLinecap="round"/>
                            <ellipse cx="205" cy="281" rx="105" ry="15" fill="#e2e6ff"/>
                        </svg>
                            </div>
                        </div>
                        <div className="col-12 col-md-auto">
                            <div className="cpm-divider" aria-hidden="true" />
                        </div>
                        <div className="col-12 col-md">
                        <section className="cpm-content">
                        <div className="cpm-badge">
                            <i aria-hidden="true">!</i>
                            Data Not Available
                        </div>
                        <h1>CPM data not available.</h1>
                        <p className="cpm-subtitle">Please run CPM analysis first.</p>
                        <div className="cpm-actions">
                            <button 
                                type="button" 
                                className="btn run" 
                                onClick={handleRunCPM}
                                disabled={loading}
                            >
                                <span className="play" aria-hidden="true">
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                        '▶'
                                    )}
                                </span>
                                {loading ? 'Running CPM...' : 'Run CPM Analysis'}
                                <span className="arrow" aria-hidden="true">→</span>
                            </button>
                            <button 
                                type="button" 
                                className="btn back" 
                                onClick={() => props.history?.goBack()}
                                disabled={loading}
                            >
                                <span className="backarr" aria-hidden="true">←</span>
                                Go Back
                            </button>
                        </div>
                        </section>
                        </div>
                    </div>
                </div>
                {/* Reset Dates Modal */}
                <Modal show={showResetModal} onHide={() => { setValidated(false); setModalError(null); setShowResetModal(false); }} centered className="cpm-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>Run CPM Analysis</Modal.Title>
                    </Modal.Header>
                    <Form noValidate validated={validated} onSubmit={handleRunCPMWithDates}>
                        <Modal.Body>
                            {modalError && (
                                <div className="cpm-modal-error mb-3 p-3 rounded bg-danger bg-opacity-10 border border-danger border-opacity-25 text-danger">
                                    <div className="d-flex align-items-start gap-2">
                                        <i className="fa fa-exclamation-circle mt-1" aria-hidden="true" />
                                        <span>{modalError}</span>
                                    </div>
                                </div>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Start Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={newStartDate}
                                    onChange={(e) => setNewStartDate(e.target.value)}
                                    required
                                />
                                {!newStartDate && (
                                    <Form.Control.Feedback type="invalid">
                                        Please select a start date.
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">End Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={newEndDate}
                                    onChange={(e) => setNewEndDate(e.target.value)}
                                    required
                                />
                                {!newEndDate && (
                                    <Form.Control.Feedback type="invalid">
                                        Please select an end date.
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => { setValidated(false); setModalError(null); setShowResetModal(false); }}>
                                Cancel
                            </Button>
                            <Button type="submit" className="btn btn-solid-blue fw-bold" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Running...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-play me-2"></i> Run CPM
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        );
    }

    const filteredActivities = getFilteredActivities();
    const stages = getUniqueStages();
    const stageStatuses = getStageStatuses();

    // ──────────────────────────────────────────────────────────────────────────
    // Tab: Milestones
    // ──────────────────────────────────────────────────────────────────────────
    const renderMilestoneTab = () => {
        if (milestonesLoading) {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" style={{ color: "#7e22ce" }}>
                        <span className="sr-only">Loading milestones...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">Loading milestones...</p>
                </div>
            );
        }

        if (milestonesError) {
            return (
                <Card className="activity-card p-4">
                    <div className="text-center text-danger">
                        <i className="fa fa-exclamation-circle fa-3x mb-3"></i>
                        <h5>Error Loading Milestones</h5>
                        <p>{milestonesError}</p>
                        <Button variant="primary" onClick={fetchMilestones}>
                            <i className="fa fa-refresh mr-2"></i> Retry
                        </Button>
                    </div>
                </Card>
            );
        }

        return (
            <div className="milestone-tab-container">
                {/* Success Message */}
                {milestoneSuccess && (
                    <div className="milestone-toast-alert" role="alert">
                        <div className="milestone-toast-icon">
                            <i className="fa fa-check"></i>
                        </div>
                        <span className="milestone-toast-message">{milestoneSuccess}</span>
                        <button 
                            type="button" 
                            className="milestone-toast-close"
                            onClick={() => setMilestoneSuccess(null)}
                            aria-label="Dismiss"
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    </div>
                )}

                {/* ── Header Section ───────────────────────────────────────────── */}
                <div className="milestone-header-section">
                    <div className="milestone-header-left">
                        <div className="milestone-icon-wrapper">
                            <i className="fa fa-flag"></i>
                        </div>
                        <div className="milestone-header-text">
                            <h4 className="milestone-header-title">Project Milestones</h4>
                            <p className="milestone-header-subtitle">Organize tasks into milestone phases</p>
                        </div>
                    </div>
                    <button className="milestone-new-btn" onClick={handleCreateMilestone}>
                        <i className="fa fa-plus"></i>
                        <span>New Milestone</span>
                    </button>
                </div>

                {/* ── Milestones List / Empty State ────────────────────────────── */}
                {milestones.length === 0 ? (
                    <div className="milestone-empty-state">
                        <i className="fa fa-flag-checkered"></i>
                        <h5>No Milestones Yet</h5>
                        <p>Create your first milestone to start organizing tasks into phases</p>
                        <button className="milestone-new-btn2" onClick={handleCreateMilestone}>
                            <i className="fa fa-plus"></i>
                            <span>Create First Milestone</span>
                        </button>
                    </div>
                ) : (
                    <div className="milestones-list-redesign">
                        {milestones.map((milestone) => {
                            const linkedTasks = (milestone.task_ids || []).map(taskId => getTaskById(taskId)).filter(Boolean);
                            const isExpanded = expandedMilestones[milestone._id] !== false;
                            
                            return (
                                <div key={milestone._id} className="milestone-card-redesign">
                                    {/* ── Card Header ──────────────────────────────── */}
                                    <div className="milestone-card-header">
                                        <div className="milestone-header-content">
                                            <div className="milestone-flag-icon">
                                                <i className="fa fa-flag"></i>
                                            </div>
                                            <h5 className="milestone-name">{milestone.name}</h5>
                                            {(milestone.start_date || milestone.end_date) && (
                                                <div className="milestone-dates">
                                                    {milestone.start_date && (
                                                        <span className="milestone-date-item">
                                                            <i className="fa fa-calendar-alt"></i>
                                                            <span>Start: {milestone.start_date}</span>
                                                        </span>
                                                    )}
                                                    {milestone.end_date && (
                                                        <span className="milestone-date-item">
                                                            <i className="fa fa-calendar-check"></i>
                                                            <span>End: {milestone.end_date}</span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="milestone-actions">
                                            <button
                                                className="milestone-action-btn milestone-manage-btn"
                                                onClick={() => handleOpenTaskLinkModal(milestone)}
                                                title="Manage Tasks"
                                            >
                                                <i className="fa fa-link"></i>
                                                <span>Manage Tasks</span>
                                            </button>
                                            <button
                                                className="milestone-action-btn milestone-edit-btn"
                                                onClick={() => handleEditMilestone(milestone)}
                                                title="Edit Milestone"
                                            >
                                                <i className="fa fa-edit"></i>
                                                <span>Edit Milestone</span>
                                            </button>
                                            <button
                                                className="milestone-action-btn milestone-delete-btn"
                                                onClick={() => handleDeleteMilestone(milestone._id)}
                                                title="Delete Milestone"
                                            >
                                                <i className="fa fa-trash"></i>
                                                <span>Delete Milestone</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* ── Linked Tasks Section ──────────────────────── */}
                                    <div className="milestone-tasks-section">
                                        <button
                                            className="milestone-tasks-toggle"
                                            onClick={() => toggleMilestoneExpansion(milestone._id)}
                                        >
                                            <div className="milestone-tasks-toggle-left">
                                                <i className="fa fa-list"></i>
                                                <span className="milestone-tasks-label">Linked Tasks</span>
                                                <span className="milestone-tasks-count">({linkedTasks.length})</span>
                                            </div>
                                            <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} milestone-tasks-chevron`}></i>
                                        </button>

                                        {isExpanded && (
                                            <div className="milestone-tasks-list">
                                                {linkedTasks.length === 0 ? (
                                                    <div className="milestone-tasks-empty">
                                                        <i className="fa fa-info-circle"></i>
                                                        <p>No tasks linked yet.</p>
                                                        <p className="milestone-tasks-empty-hint">
                                                            Click "Manage Tasks" to add tasks.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    linkedTasks.map(task => (
                                                        <div key={task.activity_id} className="milestone-task-item">
                                                            <div className="milestone-task-left">
                                                                <span className={`milestone-task-badge ${task.critical ? 'milestone-task-badge-critical' : 'milestone-task-badge-normal'}`}>
                                                                    {task.activity_id}
                                                                </span>
                                                                <div className="milestone-task-details">
                                                                    <span className="milestone-task-name">{task.label}</span>
                                                                    {task.stage_label && (
                                                                        <span className="milestone-task-stage">{task.stage_label}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="milestone-unlink-btn"
                                                                onClick={() => handleUnlinkTask(milestone, String(task.activity_id))}
                                                                title="Unlink Task"
                                                            >
                                                                <i className="fa fa-unlink"></i>
                                                                <span>Unlink Task</span>
                                                            </button>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // ──────────────────────────────────────────────────────────────────────────
    // Tab: Tasks
    // ──────────────────────────────────────────────────────────────────────────
    const renderTasksTab = () => (
        <>
                {/* Stage Filter */}
            {stages.length > 0 && (
                <Card className="activity-card p-3 mb-4">
                    <div className="stage-filter-wrapper">
                        <span className="stage-filter-label">Filter by Stage:</span>
                        <div className="stage-filter-badges">
                            {stages.map((stage, idx) => {
                                const status = stageStatuses[stage] || "not-started";
                                return (
                                    <span
                                        key={idx}
                                        className={`stage-filter-badge badge-${status} ${filterStage === stage ? "badge-active" : "badge-inactive"}`}
                                        onClick={() => setFilterStage(stage)}
                                    >
                                        {stage}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </Card>
            )}


            {/* Activities Table */}
            <Card className="activity-card p-4">
                <div className="cpm-activities-header mb-4">
                    <div className="activities-header-left">
                        
                        <div className="activities-text-wrapper">
                            
                            <h5 className="font-weight-bold mb-0 activities-title heading"><i className="fa fa-tasks"></i>  {filteredActivities.length} Activities {filterStage !== "all" && `in ${filterStage}`} </h5>
                            {/* <span className="text-muted text-sm activities-count-text">
                                {filteredActivities.length} activities {filterStage !== "all" && `in ${filterStage}`}
                            </span> */}
                        </div>
                    </div>
                    
                </div>

                {/* Progress bar for the selected stage (visible only when a specific stage is selected) */}
                {filterStage !== "all" && stageProgressList.length > 0 && (
                    <>
                        {(() => {
                            const stageProg = stageProgressList.find(s => s.stageName === filterStage);
                            if (!stageProg) return null;
                            return (
                                <div className="mb-4 px-1">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                                            Stage Progress: {stageProg.stageName}
                                            <span className="text-muted fw-normal ms-2">({stageProg.count} activities)</span>
                                        </span>
                                        <span className={`fw-bold ${stageProg.progress === 100 ? 'text-success' : stageProg.progress > 0 ? 'text-primary' : 'text-muted'}`}>
                                            {stageProg.progress}%
                                        </span>
                                    </div>
                                    <ProgressBar
                                        now={stageProg.progress}
                                        variant={stageProg.progress === 100 ? 'success' : stageProg.progress > 0 ? 'info' : 'secondary'}
                                        className="progress-cpm-bar"
                                        style={{ height: "10px", borderRadius: "5px" }}
                                    />
                                </div>
                            );
                        })()}
                    </>
                )}

                {filteredActivities.length > 0 ? (
                    <div className="table-responsive">
                        <Table hover className="custom-detail-table cpm-activities-table cpm-tasks-table">
<thead>
                                <tr>
                                    <th>ID</th>
                                    
                                    <th>Activity</th>
                                      
                                    <th className="text-center">Duration (days)</th>
                                    <th className="d-none d-sm-table-cell">Early Start</th>
                                    <th className="d-none d-sm-table-cell">Early Finish</th>
                                    <th className="d-none d-lg-table-cell">Late Start</th>
                                    <th className="d-none d-lg-table-cell">Late Finish</th>
                                    <th className="d-none d-lg-table-cell">Milestone</th>
                                    <th className="text-center">Float</th>
                                    <th className="text-center">Actual Start</th>
                                    <th className="text-center">Actual Finish</th>
                                  
                                      
                                </tr>
                            </thead>
                            <tbody>
                                {filteredActivities.map((activity, idx) => {
                                    const aid = activity.id || idx + 1;
                                    return (
                                            <tr 
                                                key={idx}
                                                className={activity.critical ? "critical-activity" : ""}
                                                style={activity.color ? { borderLeft: `4px solid ${activity.color}` } : {}}
                                            >
                                                <td data-label="ID">
                                                    <div className="activity-id-wrapper">
                                                        <span className="font-weight-bold activity-id">{aid}</span>
                                                         
                                                    </div>
                                                </td>
                                                {/* <td data-label="Stage" className="d-none d-sm-table-cell">
                                                    <div className="stage-badge-with-dot">
                                                        <span className="stage-dot" style={{ backgroundColor: activity.color || "#6b7280" }}></span>
                                                        <span className="stage-label-text">{activity.stage_label || activity.phase}</span>
                                                    </div>
                                                </td> */}
                                                <td data-label="Activity" className="font-weight-bold">
                                                    <div className="activity-name-cell" title={activity.label}>
                                                        <span className="activity-name-text">{activity.label}</span>
                                                    </div>
                                                </td>
                                                {/* <td data-label="Quantity" className="text-right d-none d-md-table-cell">{activity.quantity?.toFixed(2) || "-"}</td>
                                                <td data-label="Unit" className="d-none d-md-table-cell">{activity.unit || "-"}</td> */}
                                                <td data-label="Duration" className="text-center font-weight-bold">{activity.duration}</td>
                                                <td data-label="Early Start" className="d-none d-sm-table-cell">{activity.es}</td>
                                                <td data-label="Early Finish" className="d-none d-sm-table-cell">{activity.ef}</td>
                                                <td data-label="Late Start" className="d-none d-lg-table-cell">{activity.ls}</td>
<td data-label="Late Finish" className="d-none d-lg-table-cell">{activity.lf}</td>
                                                 <td data-label="Milestone" className="d-none d-lg-table-cell">
                                                     {activityIdToMilestoneMap[String(activity.activity_id)] || "-"}
                                                 </td>
                                                 <td data-label="Float" className="text-center">
                                                    <div className={`float-circle-badge ${activity.total_float === 0 ? 'float-critical' : 'float-normal'}`}>
                                                        {activity.total_float}
                                                    </div>
                                                </td>
                                                <td data-label="Actual Start" className="text-center">
                                                    {activity.actual_start ? (
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            <span className="text-success fw-bold">
                                                                <i className="fa fa-check-circle mr-1"></i>
                                                                {activity.actual_start}
                                                            </span>
                                                                  
                                                            <Button 
                                                                variant="link" 
                                                                size="sm" 
                                                                className="p-0 text-primary"
                                                                onClick={() => handleOpenActualStartModal(activity)}
                                                                title="Edit Actual Start Date"
                                                            >
                                                              <i className="fa fa-edit"></i>
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button 
                                                            variant="outline-success" 
                                                            size="sm" 
                                                            onClick={() => handleOpenActualStartModal(activity)}
                                                        >
                                                            Start
                                                        </Button>
                                                    )}
                                                </td>
                                                <td data-label="Actual Finish" className="text-center">
                                                    {activity.actual_finish ? (
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            <span className="text-danger fw-bold">
                                                                <i className="fa fa-check-circle mr-1"></i>
                                                                {activity.actual_finish}
                                                            </span>
                                                            <Button 
                                                                variant="link" 
                                                                size="sm" 
                                                                className="p-0 text-primary"
                                                                onClick={() => handleOpenActualFinishModal(activity)}
                                                                title="Edit Actual Finish Date"
                                                            >
                                                                <i className="fa fa-edit"></i>
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button 
                                                            variant="outline-danger" 
                                                            size="sm" 
                                                            onClick={() => handleOpenActualFinishModal(activity)}
                                                            disabled={!activity.actual_start}
                                                        >
                                                            Finish
                                                        </Button>
                                                    )}
                                                </td>
                                                {/* <td data-label="Gang" className="text-muted text-sm d-none d-xl-table-cell">{activity.gang || "-"}</td> */}
                                                
                                            </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <i className="fa fa-table fa-3x text-muted mb-3"></i>
                        <h5>No Activities Found</h5>
                        <p className="text-muted">No activities match the selected filter.</p>
                    </div>
                )}
            </Card>
        </>
    );

    // ──────────────────────────────────────────────────────────────────────────
    // Tab: CPM Network
    // ──────────────────────────────────────────────────────────────────────────
    const renderCPMNetworkTab = () => (
        <div className="cpm-network-tab">
            {/* Mother Network Diagram */}
            {cpmData?.mother_diagram && (
                <Card className="activity-card p-4 mb-4">
                    <div className="d-flex align-items-center gap-2 mb-4">
                        <div className="p-2 rounded bg-light-purple text-purple">
                            <i className="fa fa-sitemap" style={{ color: "#7e22ce" }}></i>
                        </div>
                        <div>
                            <h5 className="font-weight-bold mb-0 heading">Mother Network Diagram</h5>
                            <span className="text-muted text-sm">Full project CPM network</span>
                        </div>
                    </div>
                    <div className="network-diagram-wrapper text-center">
                        <a href={cpmData.mother_diagram} target="_blank" rel="noopener noreferrer">
                            <img 
                                src={cpmData.mother_diagram} 
                                alt="Mother CPM Network Diagram" 
                                className="img-fluid network-diagram-img"
                            />
                        </a>
                        <div className="mt-2">
                            <Button 
                                variant="outline-primary" 
                                size="sm" 
                                href={cpmData.mother_diagram} 
                                target="_blank"
                            >
                                <i className="fa fa-external-link mr-2"></i> Open in New Tab
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* Stage Network Diagrams */}
            {cpmData?.stage_diagrams && Object.keys(cpmData.stage_diagrams).length > 0 && (
                <Card className="activity-card p-4">
                    <div className="d-flex align-items-center gap-2 mb-4">
                        <div className="p-2 rounded bg-light-purple text-purple">
                            <i className="fa fa-project-diagram" style={{ color: "#7e22ce" }}></i>
                        </div>
                        <div>
                            <h5 className="font-weight-bold mb-0 heading">Stage Network Diagrams</h5>
                            <span className="text-muted text-sm">
                                {Object.keys(cpmData.stage_diagrams).length} stages
                            </span>
                        </div>
                    </div>
                    <div className="row g-3">
                        {Object.entries(cpmData.stage_diagrams).map(([stageLabel, diagramUrl], idx) => (
                            <div key={idx} className="col-md-6 col-lg-4">
                                <Card className="stage-diagram-card h-100">
                                    <Card.Body className="p-3">
                                        <h6 className="font-weight-bold mb-2">{stageLabel}</h6>
                                        <div 
                                            className={`stage-diagram-thumb ${expandedStageDiagram === stageLabel ? 'expanded' : ''}`}
                                            onClick={() => setExpandedStageDiagram(
                                                expandedStageDiagram === stageLabel ? null : stageLabel
                                            )}
                                        >
                                            <img 
                                                src={diagramUrl} 
                                                alt={`${stageLabel} Network Diagram`} 
                                                className="img-fluid"
                                            />
                                            {expandedStageDiagram !== stageLabel && (
                                                <div className="diagram-overlay">
                                                    <i className="fa fa-search-plus"></i>
                                                    <span>Click to expand</span>
                                                </div>
                                            )}
                                        </div>
                                        {expandedStageDiagram === stageLabel && (
                                            <div className="mt-2 text-center">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm" 
                                                    href={diagramUrl} 
                                                    target="_blank"
                                                >
                                                    <i className="fa fa-external-link mr-2"></i> Open
                                                </Button>
                                                <Button 
                                                    variant="link" 
                                                    size="sm" 
                                                    className="text-muted ms-2"
                                                    onClick={() => setExpandedStageDiagram(null)}
                                                >
                                                    Collapse
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {!cpmData?.mother_diagram && (!cpmData?.stage_diagrams || Object.keys(cpmData.stage_diagrams).length === 0) && (
                <Card className="activity-card p-4 text-center">
                    <i className="fa fa-sitemap fa-3x text-muted mb-3"></i>
                    <h5>No Network Diagrams Available</h5>
                    <p className="text-muted">Run CPM analysis to generate network diagrams.</p>
                </Card>
            )}
        </div>
    );

    // ──────────────────────────────────────────────────────────────────────────
    // Tab: Critical Path
    // ──────────────────────────────────────────────────────────────────────────
    const renderCriticalPathTab = () => {
        const criticalStages = cpmData?.stages?.filter(s => s.critical) || [];
        const criticalActivities = cpmData?.all_activities?.filter(a => a.critical) || [];
        const totalActivities = cpmData?.all_activities?.length || 0;
        const totalStages = cpmData?.stages?.length || 0;

        return (
            <div className="critical-path-tab">
                {/* Summary Cards */}
                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <Card className="activity-card p-3 text-center h-100">
                            <div className="text-muted text-xs mb-1">Critical Path Stages</div>
                            <h5 className="font-weight-bold mb-0 text-danger">{criticalStages.length} / {totalStages}</h5>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="activity-card p-3 text-center h-100">
                            <div className="text-muted text-xs mb-1">Critical Activities</div>
                            <h5 className="font-weight-bold mb-0 text-danger">{criticalActivities.length} / {totalActivities}</h5>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="activity-card p-3 text-center h-100">
                            <div className="text-muted text-xs mb-1">Total Project Duration</div>
                            <h5 className="font-weight-bold mb-0">{cpmData?.total_days || 0} days</h5>
                        </Card>
                    </div>
                </div>

                {/* Critical Path Chain */}
                {cpmData?.critical_path && cpmData.critical_path.length > 0 && (
                    <Card className="activity-card p-4 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-4">
                            <div className="p-2 rounded bg-light-purple text-purple">
                                <i className="fa fa-link" style={{ color: "#dc2626" }}></i>
                            </div>
                            <div>
                                <h5 className="font-weight-bold mb-0 heading">Critical Path Chain</h5>
                                <span className="text-muted text-sm">
                                    {cpmData.critical_path.length} stages in sequence
                                </span>
                            </div>
                        </div>
                        <div className="critical-path-flow">
                            {cpmData.critical_path.map((stageId, idx) => {
                                const stageDetail = cpmData.stages?.find(s => s.stage_id === stageId);
                                return (
                                    <div key={idx} className="critical-path-node-wrapper">
                                        <div className={`critical-path-node ${stageDetail?.critical ? 'is-critical' : ''}`}>
                                            <div className="node-phase-badge" style={{
                                                backgroundColor: '#0e3dc8'
                                            }}>
                                                {stageDetail?.phase || 'Stage'}
                                            </div>
                                            <div className="node-label">{stageDetail?.stage_label || stageId}</div>
                                            <div className="node-duration">{stageDetail?.duration || '-'} days</div>
                                            {stageDetail?.start && stageDetail?.end && (
                                                <div className="node-dates text-muted">
                                                    {stageDetail.start} - {stageDetail.end}
                                                </div>
                                            )}
                                        </div>
                                        {idx < cpmData.critical_path.length - 1 && (
                                            <div className="critical-path-arrow">
                                                <i className="fa fa-arrow-right"></i>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                )}

                {/* Critical Activities Table */}
                {criticalActivities.length > 0 && (
                    <Card className="activity-card p-4">
                        <div className="d-flex align-items-center gap-2 mb-4">
                            <div className="p-2 rounded" style={{ backgroundColor: "#fef2f2" }}>
                                <i className="fa fa-exclamation-triangle" style={{ color: "#dc2626" }}></i>
                            </div>
                            <div>
                                <h5 className="font-weight-bold mb-0 heading">Critical Activities (Zero Float)</h5>
                                <span className="text-muted text-sm">
                                    These activities directly impact the project completion date
                                </span>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <Table hover className="custom-detail-table cpm-activities-table">
                                <thead>
                                    <tr>
                                        <th className="d-none d-sm-table-cell">Stage</th>
                                        <th>Activity</th>
                                        <th className="text-center">Duration</th>
                                        <th className="d-none d-sm-table-cell">Early Start</th>
                                        <th className="d-none d-sm-table-cell">Early Finish</th>
                                        <th className="d-none d-lg-table-cell">Late Start</th>
                                        <th className="d-none d-lg-table-cell">Late Finish</th>
                                        <th className="text-center">Float</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {criticalActivities.map((activity, idx) => (
                                        <tr key={idx} className="critical-activity">
                                            <td data-label="Stage" className="d-none d-sm-table-cell">
                                                <Badge bg="secondary">
                                                    {activity.stage_label || activity.phase}
                                                </Badge>
                                            </td>
                                            <td data-label="Activity" className="font-weight-bold activity-name-cell">{activity.label}</td>
                                            <td data-label="Duration" className="text-center font-weight-bold">{activity.duration}</td>
                                            <td data-label="Early Start" className="d-none d-sm-table-cell">{activity.es}</td>
                                            <td data-label="Early Finish" className="d-none d-sm-table-cell">{activity.ef}</td>
                                            <td data-label="Late Start" className="d-none d-lg-table-cell">{activity.ls}</td>
                                            <td data-label="Late Finish" className="d-none d-lg-table-cell">{activity.lf}</td>
                                            <td data-label="Float" className="text-center">
                                                <Badge bg="danger">{activity.total_float}</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card>
                )}

                {criticalActivities.length === 0 && (
                    <Card className="activity-card p-4 text-center">
                        <i className="fa fa-check-circle fa-3x text-success mb-3"></i>
                        <h5>No Critical Activities</h5>
                        <p className="text-muted">All activities have positive float.</p>
                    </Card>
                )}
            </div>
        );
    };
 
    // ──────────────────────────────────────────────────────────────────────────
    // Tab: Procurement
    // ──────────────────────────────────────────────────────────────────────────
    const renderProcurementTab = () => {
        // Show loading spinner while fetching data
        if (procurementLoading) {
            return (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" variant="purple" />
                    <span className="ms-3">Loading procurement data...</span>
                </div>
            );
        }

        // If no data yet (initial loading or error), show empty state
        if (!procurementSections || !procurementTotal) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-shopping-cart fa-3x text-muted mb-3"></i>
                    <h5>No Procurement Data</h5>
                    {procurementError ? (
                        <p className="text-danger">{procurementError}</p>
                    ) : (
                        <p className="text-muted">Run estimation modules to generate procurement data.</p>
                    )}
                </div>
            );
        }

        const sections = procurementSections;
        const total = procurementTotal;

        // Collect all unique stage names from procurement data (deduplicated)
        const allProcurementStages = [...new Set(
            sections.flatMap(section => section.stages.map(s => s.name))
        )];

        // Filter stages based on selected filter
        const filteredSections = sections.map(section => ({
            ...section,
            stages: procurementFilterStage === "all"
                ? section.stages
                : section.stages.filter(s => s.name === procurementFilterStage)
        })).filter(section => section.stages.length > 0);

        const hasData = filteredSections.length > 0 && filteredSections.some(s => s.stages.length > 0);

        return (
            <div className="procurement-tab">
                {/* Stage Filter */}
                {allProcurementStages.length > 0 && (
                    <Card className="activity-card p-3 mb-4">
                        <div className="stage-filter-wrapper">
                            <span className="stage-filter-label">Filter by Stage:</span>
                            <div className="stage-filter-badges">
                                <span
                                    className={`stage-filter-badge badge-not-started ${procurementFilterStage === "all" ? "badge-active" : "badge-inactive"}`}
                                    onClick={() => setProcurementFilterStage("all")}
                                >
                                    All
                                </span>
                                {allProcurementStages.map((stage, idx) => (
                                    <span
                                        key={idx}
                                        className={`stage-filter-badge badge-not-started ${procurementFilterStage === stage ? "badge-active" : "badge-inactive"}`}
                                        onClick={() => setProcurementFilterStage(stage)}
                                    >
                                        {stage}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Card>
                )}

                <Card className="activity-card px-0 pt-4 pb-4 border-0 container">
                    <div className="procurement-header-row d-flex align-items-center justify-content-between mb-4 px-4">
                        <div className="d-flex align-items-center gap-2">
                            <div className="p-2 rounded" style={{ backgroundColor: "#f3e8ff" }}>
                                <i className="fa fa-shopping-cart" style={{ color: "#7e22ce" }}></i>
                            </div>
                            <div>
                                <h5 className="font-weight-bold mb-0 heading">Material Procurement Requirements</h5>
                                <span className="text-muted text-sm">
                                    {procurementFilterStage === "all" ? "All stages" : procurementFilterStage}
                                </span>
                            </div>
                        </div>
                        
                        {/* Edit/Save/Cancel buttons */}
                        <div className="d-flex gap-2">
                            {!isEditingProcurement ? (
                                <button 
                                    className="action-pill-btn action-pill-btn-primary"
                                    onClick={handleEditProcurement}
                                >
                                    <i className="fa fa-edit"></i>
                                    <span className="btn-text">Edit Procurement Data</span>
                                </button>
                            ) : (
                                <>
                                    <button 
                                        className="action-pill-btn action-pill-btn-primary"
                                        onClick={handleSaveProcurement}
                                        disabled={procurementSaving}
                                    >
                                        {procurementSaving ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="mr-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-save mr-2"></i> Save
                                            </>
                                        )}
                                    </button>
                                    <button 
                                        className="btn btn-solid-cancel fw-bold"
                                        onClick={handleCancelEdit}
                                        disabled={procurementSaving}
                                    >
                                        <i className="fa fa-times mr-2"></i> Cancel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Success message */}
                    {procurementSaveSuccess && (
                        <div className="milestone-toast-alert" role="alert">
                            <div className="milestone-toast-icon">
                                <i className="fa fa-check"></i>
                            </div>
                            <span className="milestone-toast-message">{procurementSaveSuccess}</span>
                            <button 
                                type="button" 
                                className="milestone-toast-close"
                                onClick={() => setProcurementSaveSuccess(null)}
                                aria-label="Dismiss"
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                    )}

                    {hasData ? (
                        <div className="table-responsive">
<div className="material-table-wrapper">
                                <table className="material-requirement-table custom-detail-table">
                                <thead>
                                    <tr>
                                        <th className="material-module-cell">Module</th>
                                        <th className="material-type-cell">Type</th>
                                        {materialColumns.map((col, idx) => (
                                            <th key={idx} className="material-data-cell">{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSections.map((section, sectionIdx) => (
                                        <React.Fragment key={sectionIdx}>
                                            {/* Section header row (e.g. GROUND FLOOR) */}
                                            <tr className="procurement-section-header">
                                                <td colSpan={materialColumns.length + 2}>
                                                    {section.sectionTitle}
                                                </td>
                                            </tr>
                                            {section.stages.map((stage, stageIdx) => (
                                                <React.Fragment key={stageIdx}>
                                                    {stage.rows.map((row, rowIdx) => {
                                                        const isEditable = isEditingProcurement && row.label !== "Estimated";
                                                        const editKey = `${section.floorLabel}|${stage.name}|${row.label}`;
                                                        
                                                        return (
                                                            <tr key={rowIdx}>
                                                                <td className="material-module-cell" data-label="Module">{stage.name}</td>
                                                                <td className="material-type-cell" data-label="Type">
                                                                    <span className={`material-type-badge type-${row.label.toLowerCase()}`}>
                                                                        {row.label}
                                                                    </span>
                                                                </td>
                                                                {materialColumns.map((col, colIdx) => {
                                                                    const displayValue = isEditable 
                                                                        ? (procurementEditData[editKey]?.[colIdx] ?? row.values[colIdx])
                                                                        : row.values[colIdx];
                                                                    
                                                                    // Check if Estimated has a value for this column
                                                                    const estimatedRow = stage.rows.find(r => r.label === "Estimated");
                                                                    const hasEstimated = estimatedRow?.values[colIdx] != null;
                                                                    
                                                                    return (
                                                                        <td key={colIdx} className="material-data-cell" data-label={col}>
                                                                            {isEditable && hasEstimated ? (
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm"
                                                                                    style={{ minWidth: "80px" , textAlign: "center"}}
                                                                                    value={displayValue ?? ''}
                                                                                    onChange={(e) => handleProcurementCellChange(
                                                                                        section.floorLabel,
                                                                                        stage.name,
                                                                                        row.label,
                                                                                        colIdx,
                                                                                        e.target.value
                                                                                    )}
                                                                                    onWheel={(e) => e.currentTarget.blur()}
                                                                                    placeholder="0"
                                                                                    min="0"
                                                                                    step="0.01"
                                                                                />
                                                                            ) : (
                                                                                displayValue != null ? displayValue : "-"
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                    {/* Total rows */}
                                    <tr className="procurement-total-header">
                                        <td colSpan={materialColumns.length + 2}>
                                            Total (Estimated)
                                        </td>
                                    </tr>
                                    {total.rows.map((row, rowIdx) => (
                                        <tr key={rowIdx} className="procurement-total-row">
                                            <td className="material-module-cell" data-label="Module">{total.label}</td>
                                            <td className="material-type-cell" data-label="Type">
                                                <span className={`material-type-badge type-${row.label.toLowerCase()}`}>
                                                    {row.label}
                                                </span>
                                            </td>
                                            {materialColumns.map((col, colIdx) => (
                                                <td key={colIdx} className="material-data-cell" data-label={col}>
                                                    {row.values[colIdx] != null ? row.values[colIdx] : "-"}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <i className="fa fa-shopping-cart fa-3x text-muted mb-3"></i>
                            <h5>No Procurement Data</h5>
                            <p className="text-muted">No procurement data available for the selected filter.</p>
                        </div>
                    )}
                </Card>
            </div>
        );
    };

    // ──────────────────────────────────────────────────────────────────────────
    // Tab: Labour
    // ──────────────────────────────────────────────────────────────────────────
    const renderLabourTab = () => {
        // Show loading spinner while fetching data
        if (labourLoading) {
            return (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" variant="purple" />
                    <span className="ms-3">Loading labour data...</span>
                </div>
            );
        }

        // If no data yet (initial loading or error), show empty state
        if (!labourSections || labourColumns.length === 0) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-users fa-3x text-muted mb-3"></i>
                    <h5>No Labour Data</h5>
                    {labourError ? (
                        <p className="text-danger">{labourError}</p>
                    ) : (
                        <p className="text-muted">Run estimation modules to generate labour summary data.</p>
                    )}
                </div>
            );
        }

        const total = labourTotal;
        const totalColCount = labourColumns.length + 6;

        // Collect all unique stage names from labour data (deduplicated)
        const allLabourStages = [...new Set(
            labourSections.flatMap(section => section.stages.map(s => s.name))
        )];

        // Filter stages based on selected filter
        const filteredLabourSections = labourSections.map(section => ({
            ...section,
            stages: labourFilterStage === "all"
                ? section.stages
                : section.stages.filter(s => s.name === labourFilterStage)
        })).filter(section => section.stages.length > 0);

        if (!total) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-users fa-3x text-muted mb-3"></i>
                    <h5>No Labour Data</h5>
                    <p className="text-muted">Run estimation modules to generate labour summary data.</p>
                </div>
            );
        }

        const getItemKey = (section, stage, item) => `${section.floorLabel}|${stage.name}|${item.label}`;
        const getAllocSource = () => isEditingLabour ? labourEditData : labourAllocated;

        const getMatchingActivity = (itemLabel) => {
            if (!cpmData?.all_activities) return null;
            return cpmData.all_activities.find(act => act.label === itemLabel);
        };

        const getDisplayValueForAllocated = (key, colIdx) => {
            const val = getAllocSource()[key]?.[colIdx];
            return val != null && val !== '' ? Number(val) : null;
        };

        const getStageAllocated = (section, stage, colIdx) => {
            let sum = 0;
            let hasValue = false;
            stage.items.forEach(item => {
                const val = getAllocSource()[getItemKey(section, stage, item)]?.[colIdx];
                if (val != null && val !== '' && !isNaN(Number(val))) {
                    sum += Number(val);
                    hasValue = true;
                }
            });
            return hasValue ? sum : null;
        };

        const allocatedGrandTotal = labourColumns.map((col, colIdx) => {
            let sum = 0;
            let hasValue = false;
            labourSections.forEach(section => {
                section.stages.forEach(stage => {
                    const stageSum = getStageAllocated(section, stage, colIdx);
                    if (stageSum != null) {
                        sum += stageSum;
                        hasValue = true;
                    }
                });
            });
            return hasValue ? sum : null;
        });

        const estimatedTotalRow = total.rows.find(r => r.label === "Estimated");
        const formatVal = (val) => val != null ? Math.round(Number(val)).toString() : "-";

        // Calculate total allocated and remaining labour per labour type
        const currentAvailable = (isEditingLabour || isEditingAvailableLabour) ? availableLabourEdit : availableLabour;
        
        // allocatedGrandTotal is an array matching labourColumns order
        // Calculate remaining per labour type
        const remainingLabourByType = {};
        const isOverAllocatedByType = {};
        
        labourColumns.forEach((labourType, colIdx) => {
            const allocated = allocatedGrandTotal[colIdx] || 0;
            const normalizedKey = normalizeLabourType(labourType);
            const available = getAvailableLabourCount(normalizedKey, currentAvailable);
            
           // console.log(`Labour Type: "${labourType}" | Normalized: "${normalizedKey}" | Available: ${available} | Allocated: ${allocated}`);
            
            remainingLabourByType[normalizedKey] = available - allocated;
            isOverAllocatedByType[normalizedKey] = allocated > available;
        });
        
        // console.log('=== Remaining Labour Calculation ===');
        // console.log('currentAvailable:', currentAvailable);
        // console.log('remainingLabourByType:', remainingLabourByType);
        // console.log('isOverAllocatedByType:', isOverAllocatedByType);
        
        const isOverAllocated = Object.values(isOverAllocatedByType).some(v => v);

        return (
            <div className="labour-tab">
                

                <Card className="activity-card px-0 pt-4 pb-4 border-0 container">
                    
                    {/* Available Labour Section - Redesigned */}
                    <div className="available-labour-section mb-4 px-4" style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e9ecef', padding: '24px' }}>
                        {/* Row 1: Labour Available for Allocation */}
                        <div className="labour-available-row mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div style={{ 
                                        width: '56px', 
                                        height: '56px', 
                                        backgroundColor: '#eff6ff', 
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <i className="fa fa-users" style={{ fontSize: '24px', color: '#2563eb' }}></i>
                                    </div>
                                    <h5 className="fw-bold mb-0" style={{ fontSize: '1.15rem', color: '#1f2937' }}>Labour Available for Allocation</h5>
                                </div>
                                
                                {isEditingAvailableLabour ? (
                                    <div className="d-flex gap-2">
                                        <button 
                                            className="action-pill-btn action-pill-btn-primary"
                                            onClick={handleSaveAvailableLabour}
                                            disabled={availableLabourSaving}
                                        >
                                            {availableLabourSaving ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <>
                                                    <i className="fa fa-save mr-2"></i>
                                                    Save
                                                </>
                                            )}
                                        </button>
                                        <button 
                                            className="btn btn-solid-cancel fw-bold"
                                            onClick={handleCancelAvailableLabour}
                                            disabled={availableLabourSaving}
                                        >
                                            <i className="fa fa-times mr-2"></i> Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        className="action-pill-btn action-pill-btn-primary"
                                        onClick={handleEditAvailableLabour}
                                    >
                                        <i className="fa fa-edit me-1"></i> Edit Available Labour
                                    </button>
                                )}
                            </div>
                            
                            {/* Labour Type Cards */}
                            <div className="row g-3" style={{ justifyContent: 'center' }}>
                                {[
                                    { key: 'helper', label: 'Helper', icon: 'fa-user', color: '#2563eb', bgColor: '#eff6ff' },
                                    { key: 'mason', label: 'Mason', icon: 'fa-hammer', color: '#7c3aed', bgColor: '#f5f3ff' },
                                    { key: 'mixerOperator', label: 'Mixer Operator', icon: 'fa-cogs', color: '#f8a100', bgColor: '#f0fdf4' },
                                    { key: 'barBender', label: 'Bar Bender', icon: 'fa-wrench', color: '#dc2626', bgColor: '#fef2f2' },
                                    { key: 'carpenter', label: 'Carpenter', icon: 'fa-tools', color: '#06b6d4', bgColor: '#ecfeff' }
                                ].map(({ key, label, icon, color, bgColor }) => (
                                    <div key={key} className="col-lg-2 col-md-4 col-6">
                                        <div style={{
                                            padding: '16px',
                                            borderRadius: '12px',
                                            border: '1px solid #e5e7eb',
                                            backgroundColor: '#fff',
                                            transition: 'all 0.2s',
                                            height: '100%'
                                             
                                        }}>
                                            <div className="d-flex align-items-center gap-2 mb-2" style={{ justifyContent: 'center' }}>
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    backgroundColor: bgColor,
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <i className={`fa ${icon}`} style={{ fontSize: '18px', color: color }}></i>
                                                </div>
                                                <span style={{ fontSize: '1.20rem', fontWeight: '600', color: color }}>{label}</span>
                                            </div>
                                            {isEditingAvailableLabour ? (
                                                <input
                                                    type="number"
                                                    className="labour-input"
                                                    
                                                    value={availableLabourEdit[key] || ''}
                                                    onChange={(e) => {
                                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                                        setAvailableLabourEdit(prev => ({ ...prev, [key]: digitsOnly === '' ? 0 : Number(digitsOnly) }));
                                                    }}
                                                    onKeyDown={(e) => ['.', ',', 'e', 'E', '-', '+'].includes(e.key) && e.preventDefault()}
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                    placeholder="0"
                                                    min="0"
                                                    step="1"
                                                />
                                            ) : (
                                                <div style={{ 
                                                    fontSize: '1.4rem', 
                                                    fontWeight: '700', 
                                                    color: '#1f2937',
                                                    textAlign: 'center',
                                                }}>
                                                    {availableLabour[key] || 0}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Row 2: Allocated Labour */}
                        <div className="labour-allocated-row mb-3 pb-3" style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <div style={{ 
                                    width: '48px', 
                                    height: '48px', 
                                    backgroundColor: '#f0fdf4', 
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <i className="fa fa-check-circle" style={{ fontSize: '20px', color: '#16a34a' }}></i>
                                </div>
                                <h6 className="fw-bold mb-0" style={{ fontSize: '1.125rem', color: '#1f2937' }}>Allocated Labour</h6>
                                <div className="d-flex align-items-center gap-3 flex-wrap ms-auto">
                                    {[
                                        { key: 'helper', label: 'Helper', color: '#2563eb' },
                                        { key: 'mason', label: 'Mason', color: '#7c3aed' },
                                        { key: 'mixerOperator', label: 'Mixer Operator', color: '#f8a100' },
                                        { key: 'barBender', label: 'Bar Bender', color: '#dc2626' },
                                        { key: 'carpenter', label: 'Carpenter', color: '#06b6d4' }
                                    ].map(({ key, label, color }, idx) => (
                                        <div key={key} className="d-flex align-items-center gap-1">
                                            <span style={{ fontSize: '1.2rem', fontWeight: '600', color: color }}>{label}:</span>
                                            <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>
                                                {allocatedGrandTotal[idx] || 0}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Row 3: Remaining Labour */}
                        <div className="labour-remaining-row">
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <div style={{ 
                                    width: '48px', 
                                    height: '48px', 
                                    backgroundColor: '#eff6ff', 
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <i className="fa fa-clock" style={{ fontSize: '20px', color: '#2563eb' }}></i>
                                </div>
                                <h6 className="fw-bold mb-0" style={{ fontSize: '1.125rem', color: '#1f2937' }}>Remaining Labour</h6>
                                <div className="d-flex align-items-center gap-3 flex-wrap ms-auto">
                                    {[
                                        { key: 'helper', label: 'Helper', color: '#2563eb' },
                                        { key: 'mason', label: 'Mason', color: '#7c3aed' },
                                        { key: 'mixerOperator', label: 'Mixer Operator', color: '#f8a100' },
                                        { key: 'barBender', label: 'Bar Bender', color: '#dc2626' },
                                        { key: 'carpenter', label: 'Carpenter', color: '#06b6d4' }
                                    ].map(({ key, label, color }, idx) => {
                                         const remaining = remainingLabourByType[key];
                                        const isNegative = remaining < 0;
                                        return (
                                            <div key={key} className="d-flex align-items-center gap-1">
                                                <span style={{ fontSize: '1.2rem', fontWeight: '600', color: color }}>{label}:</span>
                                                <span style={{ 
                                                    fontSize: '1rem', 
                                                    fontWeight: '600', 
                                                    color: isNegative ? '#dc2626' : '#16a34a'
                                                }}>
                                                    {remaining !== undefined ? remaining : 0}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                {isOverAllocated && (
                                    <span className="badge bg-danger ms-2">
                                        <i className="fa fa-exclamation-triangle me-1"></i> Over-allocated!
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    
                                {/* Stage Filter */}
                {allLabourStages.length > 0 && (
                    <Card className="activity-card p-3 mb-4">
                        <div className="stage-filter-wrapper">
                            <span className="stage-filter-label">Filter by Stage:</span>
                            <div className="stage-filter-badges">
                                <span
                                    className={`stage-filter-badge badge-not-started ${labourFilterStage === "all" ? "badge-active" : "badge-inactive"}`}
                                    onClick={() => setLabourFilterStage("all")}
                                >
                                    All
                                </span>
                                {allLabourStages.map((stage, idx) => (
                                    <span
                                        key={idx}
                                        className={`stage-filter-badge badge-not-started ${labourFilterStage === stage ? "badge-active" : "badge-inactive"}`}
                                        onClick={() => setLabourFilterStage(stage)}
                                    >
                                        {stage}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Card>
                )}

                    <div className="procurement-header-row d-flex align-items-center justify-content-between mb-4 px-4">
                        <div className="d-flex align-items-center gap-2">
                            <div className="p-2 rounded" style={{ backgroundColor: "#f3e8ff" }}>
                                <i className="fa fa-users" style={{ color: "#7e22ce" }}></i>
                            </div>
                            <div>
                                <h5 className="font-weight-bold mb-0 heading">Labour Requirement Summary</h5>
                                <span className="text-muted text-sm">
                                    {labourFilterStage === "all" ? "All stages" : labourFilterStage}
                                </span>
                            </div>
                        </div>

                        {/* Edit/Save/Cancel buttons */}
                        <div className="d-flex gap-2">
                            {!isEditingLabour ? (
                                <button
                                    className="action-pill-btn action-pill-btn-primary"
                                    onClick={handleEditLabour}
                                >
                                    <i className="fa fa-edit"></i>
                                    <span className="btn-text">Edit Allocated Labour</span>
                                </button>
                            ) : (
                                <>
                                    <button
                                        className="action-pill-btn action-pill-btn-primary"
                                        onClick={handleSaveLabour}
                                        disabled={labourSaving}
                                    >
                                        {labourSaving ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="mr-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-save mr-2"></i> Save
                                            </>
                                        )}
                                    </button>
                                    <button
                                        className="btn btn-solid-cancel fw-bold"
                                        onClick={handleCancelEditLabour}
                                        disabled={labourSaving}
                                    >
                                        <i className="fa fa-times mr-2"></i> Cancel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Success message */}
                    {labourSaveSuccess && (
                        <div className="milestone-toast-alert" role="alert">
                            <div className="milestone-toast-icon">
                                <i className="fa fa-check"></i>
                            </div>
                            <span className="milestone-toast-message">{labourSaveSuccess}</span>
                            <button 
                                type="button" 
                                className="milestone-toast-close"
                                onClick={() => setLabourSaveSuccess(null)}
                                aria-label="Dismiss"
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                    )}

                    <div className="table-responsive">
                        <div className="labour-table-wrapper">
                            <table className="labour-requirement-table custom-detail-table">
<thead>
                                    <tr>
                                        <th className="labour-item-cell">Item</th>
                                        <th className="labour-data-cell">Early Start</th>
                                        <th className="labour-data-cell">Early Finish</th>
                                        <th className="labour-data-cell">Actual Start</th>
                                        <th className="labour-data-cell">Actual Finish</th>
                                         <th className="labour-type-cell">Type</th>
                                        {labourColumns.map((col, idx) => (
                                            <th key={idx} className="labour-data-cell">{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLabourSections.map((section, sectionIdx) => (
                                        <React.Fragment key={sectionIdx}>
                                            {/* Section header row (e.g. GROUND FLOOR) */}
                                            <tr className="labour-section-header">
                                                <td colSpan={totalColCount}>
                                                    {section.sectionTitle}
                                                </td>
                                            </tr>
                                            {section.stages.map((stage, stageIdx) => (
                                                <React.Fragment key={stageIdx}>
                                                    {/* Module banner row (e.g. Foundation) */}
                                                    <tr className="labour-module-header">
                                                        <td colSpan={totalColCount}>{stage.name}</td>
                                                    </tr>
                                                    {stage.items.map((item, itemIdx) => {
                                                        const itemKey = getItemKey(section, stage, item);
                                                        return (
                                                            <React.Fragment key={itemIdx}>
{/* Estimated row */}
                                                                <tr className="labour-estimated-row">
                                                                    <td className="labour-item-cell" data-label="Item">{item.label}</td>
                                                                    <td className="labour-data-cell" data-label="Early Start">
                                                                        {(() => { const act = getMatchingActivity(item.label); return act?.es ?? "-"; })()}
                                                                    </td>
                                                                    <td className="labour-data-cell" data-label="Early Finish">
                                                                        {(() => { const act = getMatchingActivity(item.label); return act?.ef ?? "-"; })()}
                                                                    </td>
                                                                    <td className="labour-data-cell" data-label="Actual Start">
                                                                        {(() => { const act = getMatchingActivity(item.label); return act?.actual_start ?? "-"; })()}
                                                                    </td>
                                                                    <td className="labour-data-cell" data-label="Actual Finish">
                                                                        {(() => { const act = getMatchingActivity(item.label); return act?.actual_finish ?? "-"; })()}
                                                                    </td>
                                                                     <td className="labour-type-cell" data-label="Type">
                                                                          <span className="labour-type-badge type-estimated">Estimated</span>
                                                                      </td>
                                                                     {labourColumns.map((col, colIdx) => (
                                                                         <td key={colIdx} className="labour-data-cell" data-label={col}>
                                                                             {formatVal(item.estimated[colIdx])}
                                                                         </td>
                                                                     ))}
                                                                </tr>
{/* Allocated row */}
                                                                <tr className="labour-allocated-row">
                                                                    <td className="labour-item-cell labour-allocated-hidden-desktop" data-label="Item">{item.label}</td>
                                                                    <td className="labour-data-cell labour-allocated-hidden-desktop" data-label="Early Start">
                                                                        {(() => { const act = getMatchingActivity(item.label); return act?.es ?? "-"; })()}
                                                                    </td>
                                                                    <td className="labour-data-cell labour-allocated-hidden-desktop" data-label="Early Finish">
                                                                        {(() => { const act = getMatchingActivity(item.label); return act?.ef ?? "-"; })()}
                                                                    </td>
                                                                    <td className="labour-data-cell labour-allocated-hidden-desktop" data-label="Actual Start">
                                                                        {(() => { const act = getMatchingActivity(item.label); return act?.actual_start ?? "-"; })()}
                                                                    </td>
                                                                    <td className="labour-data-cell labour-allocated-hidden-desktop" data-label="Actual Finish">
                                                                        {(() => { const act = getMatchingActivity(item.label); return act?.actual_finish ?? "-"; })()}
                                                                    </td>
                                                                     <td className="labour-type-cell" data-label="Type">
                                                                          <span className="labour-type-badge type-allocated">Allocated</span>
                                                                      </td>
{labourColumns.map((col, colIdx) => {
                                                                    const displayValue = getDisplayValueForAllocated(itemKey, colIdx);
                                                                    const hasEstimated = item.estimated[colIdx] != null;
                                                                    return (
                                                                        <td key={colIdx} className="labour-data-cell" data-label={col}>
                                                                            {isEditingLabour && hasEstimated ? (
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm"
                                                                                    style={{ width: "60%", textAlign: "center", display: "block", margin: "0 auto" }}
                                                                                    value={displayValue ?? ''}
                                                                                    onChange={(e) => {
                                                                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                                                                        handleLabourCellChange(
                                                                                            section.floorLabel,
                                                                                            stage.name,
                                                                                            item.label,
                                                                                            colIdx,
                                                                                            digitsOnly
                                                                                        );
                                                                                    }}
                                                                                    onKeyDown={(e) => ['.', ',', 'e', 'E', '-', '+'].includes(e.key) && e.preventDefault()}
                                                                                    onWheel={(e) => e.currentTarget.blur()}
                                                                                    placeholder="0"
                                                                                    min="0"
                                                                                    step="1"
                                                                                />
                                                                            ) : (
                                                                                formatVal(displayValue)
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })}
                                                                </tr>
                                                            </React.Fragment>
                                                        );
                                                    })}
{/* Module total rows */}
                                                    <tr className="labour-module-total-row">
                                                        <td className="labour-item-cell" data-label="Item"><strong>Module Total</strong></td>
                                                        <td className="labour-data-cell" data-label="Early Start">-</td>
                                                        <td className="labour-data-cell" data-label="Early Finish">-</td>
                                                        <td className="labour-data-cell" data-label="Actual Start">-</td>
                                                        <td className="labour-data-cell" data-label="Actual Finish">-</td>
                                                         <td className="labour-type-cell" data-label="Type">
                                                              <span className="labour-type-badge type-estimated">Estimated</span>
                                                          </td>
                                                        {labourColumns.map((col, colIdx) => (
                                                            <td key={colIdx} className="labour-data-cell" data-label={col}>
                                                                {formatVal(stage.totals?.[col] ?? null)}
                                                            </td>
                                                        ))}
                                                    </tr>
<tr className="labour-module-total-row">
                                                        <td className="labour-item-cell" data-label="Item"></td>
                                                        <td className="labour-data-cell" data-label="Early Start">-</td>
                                                        <td className="labour-data-cell" data-label="Early Finish">-</td>
                                                        <td className="labour-data-cell" data-label="Actual Start">-</td>
                                                        <td className="labour-data-cell" data-label="Actual Finish">-</td>
                                                         <td className="labour-type-cell" data-label="Type">
                                                              <span className="labour-type-badge type-allocated">Allocated</span>
                                                          </td>
                                                        {labourColumns.map((col, colIdx) => (
                                                            <td key={colIdx} className="labour-data-cell" data-label={col}>
                                                                {formatVal(getStageAllocated(section, stage, colIdx))}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                </React.Fragment>
                                            ))}
                                        </React.Fragment>
                                    ))}
{/* Grand total rows */}
                                    <tr className="labour-grand-total-header">
                                        <td colSpan={totalColCount}>GRAND TOTAL</td>
                                    </tr>
                                    <tr className="labour-grand-total-row">
                                        <td className="labour-item-cell">Labour Days (days)</td>
                                        <td className="labour-data-cell" data-label="Early Start">-</td>
                                        <td className="labour-data-cell" data-label="Early Finish">-</td>
                                        <td className="labour-data-cell" data-label="Actual Start">-</td>
                                        <td className="labour-data-cell" data-label="Actual Finish">-</td>
                                         <td className="labour-type-cell">
                                              <span className="labour-type-badge type-estimated">Estimated</span>
                                          </td>
                                        {labourColumns.map((col, colIdx) => (
                                            <td key={colIdx} className="labour-data-cell" data-label={col}>
                                                {estimatedTotalRow ? formatVal(estimatedTotalRow.values[colIdx]) : "-"}
                                            </td>
                                        ))}
                                    </tr>
<tr className="labour-grand-total-row">
                                        <td className="labour-item-cell"></td>
                                        <td className="labour-data-cell" data-label="Early Start">-</td>
                                        <td className="labour-data-cell" data-label="Early Finish">-</td>
                                        <td className="labour-data-cell" data-label="Actual Start">-</td>
                                        <td className="labour-data-cell" data-label="Actual Finish">-</td>
                                         <td className="labour-type-cell">
                                               <span className="labour-type-badge type-allocated">Allocated</span>
                                           </td>
                                         {labourColumns.map((col, colIdx) => (
                                             <td key={colIdx} className="labour-data-cell" data-label={col}>
                                                 {formatVal(allocatedGrandTotal[colIdx])}
                                             </td>
                                         ))}
                                     </tr>
{/* Remaining row */}
                                      <tr className="labour-grand-total-row" style={{ backgroundColor: '#f8f9fa' }}>
                                          <td className="labour-item-cell"><strong>Remaining</strong></td>
                                          <td className="labour-data-cell" data-label="Early Start">-</td>
                                          <td className="labour-data-cell" data-label="Early Finish">-</td>
                                          <td className="labour-data-cell" data-label="Actual Start">-</td>
                                          <td className="labour-data-cell" data-label="Actual Finish">-</td>
                                           <td className="labour-type-cell">
                                                <span className="labour-type-badge type-estimated">Balance</span>
                                            </td>
                                          {labourColumns.map((col, colIdx) => (
                                              <td key={colIdx} className="labour-data-cell" data-label={col}>
                                                  {remainingLabourByType[normalizeLabourType(col)] != null ? (
                                                      <span className={remainingLabourByType[normalizeLabourType(col)] < 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                                                          {remainingLabourByType[normalizeLabourType(col)]}
                                                      </span>
                                                  ) : '—'}
                                              </td>
                                          ))}
                                      </tr>
                                 </tbody>
                            </table>
                        </div> 
                        </div>
                </Card>
                </div>
               
            
        );
        
    };

    // ──────────────────────────────────────────────────────────────────────────
    // Tab: Assessment
    // ──────────────────────────────────────────────────────────────────────────
    const renderAssessmentTab = () => {
        const assessmentStages = stages.length > 0 ? stages : [];
        const filteredAssessments = getFilteredAssessments();

        return (
            <div className="assessment-tab">
                {/* Stage Filter */}
                {assessmentStages.length > 0 && (
                    <Card className="activity-card p-3 mb-4">
                        <div className="stage-filter-wrapper">
                            <span className="stage-filter-label">Filter by Stage:</span>
                            <div className="stage-filter-badges">
                                <span
                                    className={`stage-filter-badge badge-not-started ${assessmentFilterStage === "all" ? "badge-active" : "badge-inactive"}`}
                                    onClick={() => setAssessmentFilterStage("all")}
                                >
                                    All
                                </span>
                                {assessmentStages.map((stage, idx) => (
                                    <span
                                        key={idx}
                                        className={`stage-filter-badge badge-not-started ${assessmentFilterStage === stage ? "badge-active" : "badge-inactive"}`}
                                        onClick={() => setAssessmentFilterStage(stage)}
                                    >
                                        {stage}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Card>
                )}

                {/* Assessments Card */}
                <Card className="activity-card p-4">
                    <div className="assessment-header-wrapper mb-4">
                        <div className="d-flex align-items-center gap-2">
                            <div className="p-2 rounded" style={{ backgroundColor: "#f3e8ff" }}>
                                <i className="fa fa-check-square" style={{ color: "#7e22ce" }}></i>
                            </div>
                            <div>
                                <h5 className="font-weight-bold mb-0" style={{ fontSize: "1.799rem" }}>Assessments</h5>
                                <span className="text-muted text-sm" style={{ fontSize: "0.999rem", fontWeight: "700" }}>
                                    {filteredAssessments.length} assessment{filteredAssessments.length !== 1 ? "s" : ""}
                                    {assessmentFilterStage !== "all" ? ` in ${assessmentFilterStage}` : ""}
                                </span>
                            </div>
                        </div>
                       
                    </div>

                    {filteredAssessments.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="custom-detail-table assessment-table">
                                <thead>
                                    <tr>
                                        <th>Assessment Name</th>
                                        <th>Stage</th>
                                        <th>Assigned To</th>
                                        <th>Scheduled Date</th>
                                        <th>Completed Date</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody  style={{ height : "20rem"}}>
                                    {filteredAssessments.map((assessment) => {
                                        const status = deriveAssessmentStatus(assessment);
                                        return (
                                            <tr
                                                key={assessment.id}
                                                className={status === "Overdue" ? "assessment-row-overdue" : ""}
                                            >
                                                <td data-label="Assessment Name" className="font-weight-bold">
                                                    {assessment.name}
                                                </td>
                                                <td data-label="Stage">
                                                    <Badge bg="secondary">{assessment.stage}</Badge>
                                                </td>
                                                <td data-label="Assigned To">
                                                    <span className="d-flex align-items-center gap-2">
                                                        <i className="fa fa-user text-muted"></i>
                                                        {assessment.assignedTo}
                                                    </span>
                                                </td>
                                                <td data-label="Scheduled Date">
                                                    <span className="d-flex align-items-center gap-2">
                                                        <i className="fa fa-calendar text-muted"></i>
                                                        {assessment.scheduledDate}
                                                    </span>
                                                </td>
                                                <td data-label="Completed Date">
                                                    {assessment.completedDate ? (
                                                        <span className="d-flex align-items-center gap-2">
                                                            <i className="fa fa-check-circle text-success"></i>
                                                            {assessment.completedDate}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td data-label="Status" className="text-center">
                                                    <span className={`assessment-status-badge status-${status.toLowerCase()}`}>
                                                        {status === "Overdue" && (
                                                            <span className="overdue-flag" title="Scheduled date passed">
                                                                <i className="fa fa-flag"></i>
                                                            </span>
                                                        )}
                                                        {status === "Completed" && <i className="fa fa-check-circle"></i>}
                                                        {status === "Pending" && <i className="fa fa-clock"></i>}
                                                        {status}
                                                    </span>
                                                </td>
                                                <td data-label="Action" className="text-center">
                                                    <Button variant="outline-primary" size="sm" className="btn-view-assessment" onClick={() => setViewResult(assessment.result || null)}>
                                                        <i className="fa fa-eye mr-1"></i> View
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <i className="fa fa-check-square fa-3x text-muted mb-3"></i>
                            <h5>No assessments available for this stage</h5>
                            
                            
                        </div>
                    )}
                </Card>

                {/* Add/Edit Assessment Modal */}
                <Modal show={showAssessmentModal} onHide={() => { setAssessmentFormValidated(false); setShowAssessmentModal(false); setEditingAssessment(null); }} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{editingAssessment ? "Edit Assessment" : "Add Assessment"}</Modal.Title>
                    </Modal.Header>
                    <Form noValidate validated={assessmentFormValidated} onSubmit={handleAssessmentFormSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Assessment Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter assessment name"
                                    value={assessmentForm.name}
                                    onChange={(e) => setAssessmentForm(prev => ({ ...prev, name: e.target.value }))}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    Please enter an assessment name.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Stage <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={assessmentForm.stage}
                                    onChange={(e) => setAssessmentForm(prev => ({ ...prev, stage: e.target.value }))}
                                    required
                                >
                                    <option value="">Select stage</option>
                                    {assessmentStages.map((stage, idx) => (
                                        <option key={idx} value={stage}>{stage}</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    Please select a stage.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Assigned To</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter assignee name"
                                    value={assessmentForm.assignedTo}
                                    onChange={(e) => setAssessmentForm(prev => ({ ...prev, assignedTo: e.target.value }))}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Scheduled Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={assessmentForm.scheduledDate}
                                    onChange={(e) => setAssessmentForm(prev => ({ ...prev, scheduledDate: e.target.value }))}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    Please select a scheduled date.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Completed Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={assessmentForm.completedDate}
                                    onChange={(e) => setAssessmentForm(prev => ({ ...prev, completedDate: e.target.value }))}
                                />
                                <Form.Text className="text-muted">
                                    Leave empty if not yet completed.
                                </Form.Text>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => { setAssessmentFormValidated(false); setShowAssessmentModal(false); setEditingAssessment(null); }}>
                                Cancel
                            </Button>
                            <Button type="submit" className="secondary-btn-gradient fw-bold">
                                <i className="fa fa-save mr-2"></i> {editingAssessment ? "Update" : "Save"}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={deleteConfirmId !== null} onHide={() => setDeleteConfirmId(null)} centered size="sm">
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Assessment</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete this assessment? This action cannot be undone.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>
                            Cancel
                        </Button>
                        <Button variant="danger" className="fw-bold" onClick={handleDeleteAssessment}>
                            <i className="fa fa-trash mr-2"></i> Delete
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Assessment Result Modal */}
                <AssessmentResultModal
                    show={!!viewResult}
                    onClose={() => setViewResult(null)}
                    record={viewResult}
                />
            </div>
        );
    };

    // ──────────────────────────────────────────────────────────────────────────
    // Tab: Cost Analysis
    // ──────────────────────────────────────────────────────────────────────────
    const renderCostAnalysisTab = () => {
        // Show loading spinner while fetching data
        if (costSummaryLoading) {
            return (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" variant="purple" />
                    <span className="ms-3">Loading cost summary data...</span>
                </div>
            );
        }

        // If error or no data
        if (!costSummaryRows || costSummaryRows.length === 0) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-calculator fa-3x text-muted mb-3"></i>
                    <h5>No Cost Data</h5>
                    {costSummaryError ? (
                        <p className="text-danger">{costSummaryError}</p>
                    ) : (
                        <p className="text-muted">Run estimation modules to generate cost summary data.</p>
                    )}
                </div>
            );
        }

        // Build grouped data: floor -> { modules: [], estimatedSubtotal: number }
        // The rows come in order: floor banner -> module banner -> items -> module_subtotal -> floor_total -> grand_total
        let currentFloor = '';
        const floorMap = {};
        costSummaryRows.forEach(row => {
            if (row.type === 'floor') {
                currentFloor = row['Floor / Module'];
                if (!floorMap[currentFloor]) {
                    floorMap[currentFloor] = { modules: [], estimatedSubtotal: 0 };
                }
            } else if (row.type === 'module') {
                if (!floorMap[currentFloor]) {
                    floorMap[currentFloor] = { modules: [], estimatedSubtotal: 0 };
                }
                floorMap[currentFloor].modules.push({
                    name: row['Floor / Module'],
                    estimatedCost: null,
                });
            } else if (row.type === 'module_subtotal') {
                if (floorMap[currentFloor]) {
                    const floorModules = floorMap[currentFloor].modules;
                    for (let i = floorModules.length - 1; i >= 0; i--) {
                        if (floorModules[i].estimatedCost === null) {
                            floorModules[i].estimatedCost = row['Amount (₹)'];
                            break;
                        }
                    }
                }
            } else if (row.type === 'floor_total') {
                if (floorMap[currentFloor]) {
                    floorMap[currentFloor].estimatedSubtotal = row['Amount (₹)'];
                }
            }
        });

        const floorEntries = Object.entries(floorMap);
        if (floorEntries.length === 0) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-coins fa-3x text-muted mb-3"></i>
                    <h5>No Module Data</h5>
                    <p className="text-muted">No module-level cost data available.</p>
                </div>
            );
        }

        // ── Helpers ──────────────────────────────────────────────────────────
        const formatCurrency = (value) => {
            if (value == null || isNaN(value)) return '—';
            return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        const computeDifference = (estimated, actual) => {
            if (actual === null || actual === '' || isNaN(Number(actual))) return null;
            return estimated - Number(actual);
        };

        const getActualValueFor = (key) => {
            // When editing, show the in-progress edit data; otherwise show saved actual costs
            return isEditingCosts ? (costEditData[key] ?? actualCosts[key] ?? '') : (actualCosts[key] ?? '');
        };

        const computeActualSubtotal = (floorKey, modules) => {
            let sum = 0;
            let hasValue = false;
            modules.forEach(mod => {
                const key = `${floorKey}|${mod.name}`;
                const val = getActualValueFor(key);
                if (val !== undefined && val !== '' && !isNaN(Number(val))) {
                    sum += Number(val);
                    hasValue = true;
                }
            });
            return hasValue ? sum : null;
        };

        const diffClass = (diff) => {
            if (diff === null || diff === undefined) return '';
            if (diff < 0) return 'cost-diff-negative';
            if (diff > 0) return 'cost-diff-positive';
            return 'cost-diff-zero';
        };

        const getModuleIcon = (moduleName) => {
            const name = (moduleName || '').toLowerCase();
            if (name.includes('foundation') || name.includes('footing')) return 'fa-layer-group';
            if (name.includes('beam')) return 'fa-grip-lines';
            if (name.includes('column')) return 'fa-columns';
            if (name.includes('slab')) return 'fa-th-large';
            if (name.includes('wall')) return 'fa-border-all';
            if (name.includes('stair')) return 'fa-stairs';
            return 'fa-cube';
        };

        // ── Render ──────────────────────────────────────────────────────────
        const totalModules = floorEntries.reduce((acc, [, floorData]) => acc + floorData.modules.length, 0);

        // ── Grand total (computed live, mirrors Option A design) ────────────
        const estimatedGrandTotal = floorEntries.reduce(
            (sum, [, floorData]) => sum + (Number(floorData.estimatedSubtotal) || 0),
            0
        );

        const actualGrandTotal = floorEntries.reduce((sum, [floorKey, floorData]) => {
            const sub = computeActualSubtotal(floorKey, floorData.modules);
            return sum + (sub != null ? sub : 0);
        }, 0);

        // Only show grand diff when at least one actual value exists
        const hasAnyActual = floorEntries.some(([floorKey, floorData]) =>
            floorData.modules.some(mod => {
                const val = getActualValueFor(`${floorKey}|${mod.name}`);
                return val !== undefined && val !== '' && !isNaN(Number(val));
            })
        );
        const grandDiff = hasAnyActual ? computeDifference(estimatedGrandTotal, actualGrandTotal) : null;
        const grandTotalRowKey = 'grand-total';

        return (
            <div className="cost-analysis-tab">
                <Card className="activity-card p-4">
                    <div className="cost-analysis-header-row d-flex align-items-center justify-content-between mb-4">
                        <div className="d-flex align-items-center gap-2">
                            <div className="p-2 rounded" style={{ backgroundColor: "#f3e8ff" }}>
                                <i className="fa fa-calculator" style={{ color: "#7e22ce" }}></i>
                            </div>
                            <div>
                                <h5 className="font-weight-bold mb-0 heading">Cost Analysis</h5>
                                <small className="text-muted">{totalModules} module{totalModules !== 1 ? 's' : ''} across {floorEntries.length} floor{floorEntries.length !== 1 ? 's' : ''}</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="cost-analysis-badge-pill">
                                {totalModules} Module{totalModules !== 1 ? 's' : ''} &bull; {floorEntries.length} Floor{floorEntries.length !== 1 ? 's' : ''}
                            </span>
                            
                            {/* Edit/Save/Cancel buttons */}
                            <div className="d-flex gap-2">
                                {!isEditingCosts ? (
                                    <button 
                                        className="action-pill-btn action-pill-btn-primary"
                                        onClick={handleEditCosts}
                                    >
                                        <i className="fa fa-edit"></i>
                                        <span className="btn-text">Edit Actual Costs</span>
                                    </button>
                                ) : (
                                    <>
                                        <button 
                                            className="action-pill-btn action-pill-btn-primary"
                                            onClick={handleSaveCosts}
                                            disabled={costSaving}
                                        >
                                            {costSaving ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="mr-2" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-save mr-2"></i> Save
                                                </>
                                            )}
                                        </button>
                                        <button 
                                            className="btn btn-solid-cancel fw-bold"
                                            onClick={handleCancelEditCosts}
                                            disabled={costSaving}
                                        >
                                            <i className="fa fa-times mr-2"></i> Cancel
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Success message */}
                    {costSaveSuccess && (
                        <div className="milestone-toast-alert" role="alert">
                            <div className="milestone-toast-icon">
                                <i className="fa fa-check"></i>
                            </div>
                            <span className="milestone-toast-message">{costSaveSuccess}</span>
                            <button 
                                type="button" 
                                className="milestone-toast-close"
                                onClick={() => setCostSaveSuccess(null)}
                                aria-label="Dismiss"
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                    )}

                    <div className="table-responsive">
                        <table className="cost-analysis-table">
                            <thead>
                                <tr>
                                    <th>Module</th>
                                    <th>Estimated Cost (₹)</th>
                                    <th>Actual Cost (₹)</th>
                                    <th>Difference (₹)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {floorEntries.map(([floorKey, floorData], floorIdx) => (
                                    <React.Fragment key={floorIdx}>
                                        {/* ── Floor header row ──────────────────────────── */}
                                        <tr className="cost-analysis-floor-row">
                                            <td colSpan={4}>
                                                <div className="cost-analysis-floor-header-content">
                                                    <div className="cost-analysis-icon-circle cost-analysis-icon-blue">
                                                        <i className="fa fa-building"></i>
                                                    </div>
                                                    <span className="cost-analysis-floor-title">{floorKey.replace(/_/g, ' ')}</span>
                                                </div>
                                            </td>
                                        </tr>

                                        {/* ── Module rows ──────────────────────────────── */}
                                        {floorData.modules.map((mod, modIdx) => {
                                            const actualKey = `${floorKey}|${mod.name}`;
                                            const actualVal = getActualValueFor(actualKey);
                                            const diff = computeDifference(mod.estimatedCost, actualVal);
                                            const moduleIcon = getModuleIcon(mod.name);
                                            const modRowKey = `${floorIdx}-${modIdx}`;
                                            return (
                                                <tr key={modRowKey} className="cost-analysis-module-row">
                                                    <td className="cost-analysis-module-cell" data-label="Module">
                                                        <div className="cost-analysis-icon-circle cost-analysis-icon-gray">
                                                            <i className={`fa ${moduleIcon}`}></i>
                                                        </div>
                                                        <span className="cost-analysis-module-name">{mod.name}</span>
                                                    </td>
                                                    <td className="cost-analysis-estimated-cell" data-label="Estimated Cost (₹)">
                                                        <span className="cost-analysis-cell-value">
                                                            {mod.estimatedCost != null ? formatCurrency(mod.estimatedCost) : <span className="text-muted">&mdash;</span>}
                                                        </span>
                                                    </td>
                                                    <td className="cost-analysis-actual-cell" data-label="Actual Cost (₹)">
                                                        {isEditingCosts ? (
                                                            <input
                                                                type="number"
                                                                className="cost-analysis-input"
                                                                placeholder="Enter actual cost"
                                                                value={actualVal === '' ? '' : actualVal}
                                                                onChange={(e) => handleCostCellChange(
                                                                    floorKey,
                                                                    mod.name,
                                                                    e.target.value
                                                                )}
                                                                onWheel={(e) => e.currentTarget.blur()}
                                                                min="0"
                                                                step="0.01"
                                                            />
                                                        ) : (
                                                            actualVal !== '' && actualVal != null ? (
                                                                <span className="cost-analysis-cell-value">
                                                                    {formatCurrency(actualVal)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">&mdash;</span>
                                                            )
                                                        )}
                                                    </td>
                                                    <td className={`cost-analysis-diff-cell ${diffClass(diff)}`} data-label="Difference (₹)">
                                                        {diff !== null ? (
                                                            <>
                                                                {diff < 0 ? '− ' : '+ '}
                                                                {formatCurrency(Math.abs(diff))}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">&mdash;</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {/* ── Subtotal row ─────────────────────────────── */}
                                        {(() => {
                                            const actualSubtotal = computeActualSubtotal(floorKey, floorData.modules);
                                            const diff = computeDifference(floorData.estimatedSubtotal, actualSubtotal);
                                            const subtotalRowKey = `subtotal-${floorIdx}`;
                                            return (
                                                <tr key={subtotalRowKey} className="cost-analysis-subtotal-row">
                                                    <td className="cost-analysis-subtotal-label-cell" data-label="Module">
                                                        <div className="cost-analysis-icon-circle cost-analysis-icon-green">
                                                            <i className="fa fa-calculator"></i>
                                                        </div>
                                                        <span className="cost-analysis-subtotal-label">SUBTOTAL</span>
                                                    </td>
                                                    <td className="cost-analysis-estimated-cell fw-bold" data-label="Estimated Cost (₹)">
                                                        {formatCurrency(floorData.estimatedSubtotal)}
                                                    </td>
                                                    <td className="cost-analysis-actual-cell fw-bold" data-label="Actual Cost (₹)">
                                                        {actualSubtotal !== null ? formatCurrency(actualSubtotal) : <span className="text-muted">&mdash;</span>}
                                                    </td>
                                                    <td className={`cost-analysis-diff-cell fw-bold ${diffClass(diff)}`} data-label="Difference (₹)">
                                                        {diff !== null ? (
                                                            <>
                                                                {diff < 0 ? '− ' : '+ '}
                                                                {formatCurrency(Math.abs(diff))}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">&mdash;</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })()}
                                    </React.Fragment>
                                ))}

                                {/* ── GRAND TOTAL row ─────────────────────────── */}
                                <tr key={grandTotalRowKey} className="cost-analysis-grand-total-row">
                                    <td className="cost-analysis-grand-total-label-cell" data-label="Module">
                                        <div className="cost-analysis-icon-circle cost-analysis-icon-purple">
                                            <i className="fa fa-coins"></i>
                                        </div>
                                        <span className="cost-analysis-grand-total-label">GRAND TOTAL</span>
                                    </td>
                                    <td className="cost-analysis-estimated-cell fw-bold" data-label="Estimated Cost (₹)">
                                        {formatCurrency(estimatedGrandTotal)}
                                    </td>
                                    <td className="cost-analysis-actual-cell fw-bold" data-label="Actual Cost (₹)">
                                        {hasAnyActual ? formatCurrency(actualGrandTotal) : <span className="text-muted">&mdash;</span>}
                                    </td>
                                    <td className={`cost-analysis-diff-cell fw-bold ${diffClass(grandDiff)}`} data-label="Difference (₹)">
                                        {grandDiff !== null ? (
                                            <>
                                                {grandDiff < 0 ? '− ' : '+ '}
                                                {formatCurrency(Math.abs(grandDiff))}
                                            </>
                                        ) : (
                                            <span className="text-muted">&mdash;</span>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        );
    };

    // ──────────────────────────────────────────────────────────────────────────
    // Tab: AI Analytics
    // ──────────────────────────────────────────────────────────────────────────
    const renderAiAnalyticsTab = () => (
        <div className="ai-analytics-tab">
            <Card className="activity-card p-4 text-center">
                <div className="py-5">
                    <i className="fa fa-robot fa-3x text-muted mb-3"></i>
                    <h5>AI Analytics</h5>
                    <p className="text-muted">Sections is under construction</p>
                </div>
            </Card>
        </div>
    );

    return (
        <div className="project-cpm-container main-content animate-fade-in">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3">
                    {/* <button className="d-lg-none border-0 bg-transparent d-flex align-items-center justify-content-center" style={{ padding: 0 }} onClick={() => dispatch(actions.setSideBar(true))}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                    </button> */}
                     <div>
                    <button 
                        className="back-btn-circle" 
                        onClick={() => props.history?.push(`/projects/${projectId}`)}
                        title="Back to Project Details"
                    >
                        <i className="fa fa-arrow-left"></i>
                    </button>
                </div>
                    <div className="d-flex align-items-center gap-2">
                        <div className="total-icon rounded p-2" style={{ backgroundColor: "#7e22ce", borderRadius: "0.5rem !important" }}>
                            <i className="fa fa-sitemap text-white"></i>
                        </div>
                        <div>
                            <h3 className="content-label mb-0">CPM Schedule for <span>{projectData?.name || "CPM Schedule"}</span></h3>
                            <div className="d-flex align-items-center gap-2 text-muted text-sm mt-1">
                                {/* <span>{projectData?.name || "CPM Schedule"}</span> */}
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* <Button className="btn-3d-outline fw-bold" onClick={() => history.push(`/projects/${projectId}/progress`)}>
                        <i className="fa fa-tasks mr-2"></i> Track Progress
                    </Button> */}
                    <button 
                        className={`action-pill-btn action-pill-btn-primary ${workStarted ? 'btn-work-started' : ''}`} 
                        onClick={handleStartWork}
                    >
                        {workStarted ? (
                            <>
                                <i className="fa fa-check-circle mr-2"></i>  Work Started - {workStartDate}
                             </>
                        ) : (
                            <>
                                <i className="fa fa-play mr-2"></i> Start
                            </>
                        )}
                    </button>
                    {(cpmData?.mother_diagram || cpmData?.stage_diagrams) && (
                        <button className="action-pill-btn action-pill-btn-primary" onClick={downloadAllImages}>
                            <i className="fa fa-download mr-2"></i> Download Images
                        </button>
                    )}
                    {cpmData?.excel_url && (
                        <button className="action-pill-btn action-pill-btn-primary" onClick={downloadExcel}>
                            <i className="fa fa-download mr-2"></i> Download Excel
                        </button>
                    )}
                    <button className="action-pill-btn action-pill-btn-primary" onClick={handleOpenResetModal}>
                        <i className="fa fa-calendar mr-2"></i> Reset Date
                    </button>
                    <button className="btn-refresh-circle" onClick={handleRunCPM} title="Refresh CPM">
                        <i className="fas fa-sync"></i>
                    </button>
                </div>
            </div>

            {/* Download Success Message */}
            {downloadSuccess && (
                <div className="milestone-toast-alert" role="alert">
                    <div className="milestone-toast-icon">
                        <i className="fa fa-check"></i>
                    </div>
                    <span className="milestone-toast-message">{downloadSuccess}</span>
                    <button 
                        type="button" 
                        className="milestone-toast-close"
                        onClick={() => setDownloadSuccess(null)}
                        aria-label="Dismiss"
                    >
                        <i className="fa fa-times"></i>
                    </button>
                </div>
            )}

            {/* Task Status Summary Cards — Redesigned Layout */}
            {/* {cpmData && (() => {
                const counts = getTaskStatusCounts();
                return (
                    <div className="row g-3 mb-4">
                        <div className="col-md-3 col-6">
                            <div className="cpm-status-card-new">
                                <div className="status-icon-circle" style={{ backgroundColor: "#7c3aed" }}>
                                    <i className="fa fa-tasks"></i>
                                </div>
                                <div className="status-content">
                                    <div className="status-label">Total Tasks</div>
                                    <div className="status-value" style={{ color: "#7c3aed" }}>{counts.total}</div>
                                    <div className="status-subtitle">Tasks in project</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="cpm-status-card-new">
                                <div className="status-icon-circle" style={{ backgroundColor: "#16a34a" }}>
                                    <i className="fa fa-check-circle"></i>
                                </div>
                                <div className="status-content">
                                    <div className="status-label">Completed</div>
                                    <div className="status-value" style={{ color: "#16a34a" }}>{counts.completed}</div>
                                    <div className="status-subtitle">Tasks completed</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="cpm-status-card-new">
                                <div className="status-icon-circle" style={{ backgroundColor: "#2563eb" }}>
                                    <i className="fa fa-spinner"></i>
                                </div>
                                <div className="status-content">
                                    <div className="status-label">In Progress</div>
                                    <div className="status-value" style={{ color: "#2563eb" }}>{counts.inProgress}</div>
                                    <div className="status-subtitle">Currently in progress</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="cpm-status-card-new">
                                <div className="status-icon-circle" style={{ backgroundColor: "#dc2626" }}>
                                    <i className="fa fa-link"></i>
                                </div>
                                <div className="status-content">
                                    <div className="status-label">Critical Path</div>
                                    <div className="status-value" style={{ color: "#dc2626" }}>{counts.critical}</div>
                                    <div className="status-subtitle">Affecting project duration</div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()} */}

            {/* Schedule Info Cards — Redesigned Layout */}
            {cpmData && (
                <div className="schedule-info-row mb-4">
                    <div className="schedule-info-item">
                        <div className="schedule-info-icon" style={{ backgroundColor: "#2100fa" }}>
                            <i className="fa fa-calendar"></i>
                        </div>
                        <div className="schedule-info-text">
                            <div className="schedule-info-label">Start Date</div>
                            <div className="schedule-info-value">{cpmData.start_date || "N/A"}</div>
                        </div>
                    </div>
                    <div className="schedule-info-item">
                        <div className="schedule-info-icon" style={{ backgroundColor: "#16a34a" }}>
                           <i className="far fa-calendar-check"></i>  
                        </div>
                        <div className="schedule-info-text">
                            <div className="schedule-info-label">End Date</div>
                            <div className="schedule-info-value">{cpmData.end_date || "N/A"}</div>
                        </div>
                    </div>
                    <div className="schedule-info-item">
                        <div className="schedule-info-icon" style={{ backgroundColor: "#d97706" }}>
                          <i className="far fa-clock"></i> 
                        </div>
                        <div className="schedule-info-text">
                            <div className="schedule-info-label">Total Duration</div>
                            <div className="schedule-info-value">{cpmData.total_days || 0} days</div>
                        </div>
                    </div>
                    {/* <div className="schedule-info-item">
                        <div className="schedule-info-icon" style={{ backgroundColor: "#dc2626" }}>
                            <i className="fa fa-link"></i>
                        </div>
                        <div className="schedule-info-text">
                            <div className="schedule-info-label">Critical Activities</div>
                            <div className="schedule-info-value">{getCriticalCount()} / {cpmData.all_activities?.length || 0}</div>
                        </div>
                    </div> */}
                </div>
            )}

  {/* Overall Progress Bar */}
      {progressSummary && (
        <Card className="activity-card p-4 mb-4">
          <div className="d-flex align-items-center gap-3 mb-3">
            <div
              className="p-2 rounded bg-gradient-to-br from-purple-500 to-purple-600 d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px" }}
            >
              <i className="fa fa-chart-line text-white"></i>
            </div>
            <div className="flex-grow-1 ms-5">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <h5 className="font-weight-bold mb-0">Overall Project Progress</h5>
                <span className="font-weight-bold gradient-text" style={{ fontSize: "1.25rem" }}>
                  {progressSummary.overallProgress}%
                </span>
              </div>
              <ProgressBar
                now={progressSummary.overallProgress}
                variant="purple"
                className="progress-cpm-bar"
                style={{ height: "10px", borderRadius: "5px" }}
              />
            </div>
          </div>
        </Card>
      )}

            {/* Tab Navigation */}
            <Card className="activity-card mb-4 p-0">
              <Nav variant="tabs" className="cpm-tab-nav" activeKey={activeTab} onSelect={(tab) => setActiveTab(tab)}>
    <Nav.Item>
        <Nav.Link eventKey="milestones" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-flag-checkered mr-2"></i> Milestones
        </Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link eventKey="tasks" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-tasks mr-2"></i> Tasks
        </Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link eventKey="cpm-network" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-sitemap mr-2"></i> CPM Network
        </Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link eventKey="critical-path" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-link mr-2"></i> Critical Path
        </Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link eventKey="procurement" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-shopping-cart mr-2"></i> Procurement
        </Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link eventKey="labour" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-users mr-2"></i> Labour
        </Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link eventKey="assessment" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-check-square mr-2"></i> Assessment
        </Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link eventKey="cost-analysis" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-calculator mr-2"></i> Cost Analysis
        </Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link eventKey="ai-analytics" className="cpm-tab-link" style={{ fontSize: '0.999rem' }}>
            <i className="fa fa-robot mr-2"></i> AI Analytics
        </Nav.Link>
    </Nav.Item>
</Nav>
            </Card>

            {/* Tab Content */}
            <div className="cpm-tab-content">
                {activeTab === "milestones" && renderMilestoneTab()}
                {activeTab === "tasks" && renderTasksTab()}
                {activeTab === "cpm-network" && renderCPMNetworkTab()}
                {activeTab === "critical-path" && renderCriticalPathTab()}
                {activeTab === "procurement" && renderProcurementTab()}
                {activeTab === "labour" && renderLabourTab()}
                {activeTab === "assessment" && renderAssessmentTab()}
                {activeTab === "cost-analysis" && renderCostAnalysisTab()}
                {activeTab === "ai-analytics" && renderAiAnalyticsTab()}
                
            </div>

            {/* Optional: Show warning if exists */}
            {cpmData?.warning && (
                <Card className="activity-card p-3 mt-3 border-warning">
                    <div className="d-flex align-items-center gap-2">
                        <i className="fa fa-exclamation-triangle text-warning"></i>
                        <span className="text-warning font-weight-bold">Warning:</span>
                        <span>{cpmData.warning}</span>
                    </div>
                </Card>
            )}

            {/* Reset Dates Modal */}
            <Modal show={showResetModal} onHide={() => { setValidated(false); setModalError(null); setShowResetModal(false); }} centered className="cpm-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Reset CPM Schedule Dates</Modal.Title>
                </Modal.Header>
                <Form noValidate validated={validated} onSubmit={handleRunCPMWithDates}>
                    <Modal.Body>
                        {modalError && (
                            <div className="cpm-modal-error mb-3 p-3 rounded bg-danger bg-opacity-10 border border-danger border-opacity-25 text-danger">
                                <div className="d-flex align-items-start gap-2">
                                    <i className="fa fa-exclamation-circle mt-1" aria-hidden="true" />
                                    <span>{modalError}</span>
                                </div>
                            </div>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Start Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="date"
                                value={newStartDate}
                                onChange={(e) => setNewStartDate(e.target.value)}
                                required
                            />
                            {!newStartDate && (
                                <Form.Control.Feedback type="invalid">
                                    Please select a start date.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">End Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="date"
                                value={newEndDate}
                                onChange={(e) => setNewEndDate(e.target.value)}
                                required
                            />
                            {!newEndDate && (
                                <Form.Control.Feedback type="invalid">
                                    Please select an end date.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setValidated(false); setModalError(null); setShowResetModal(false); }}>
                            Cancel
                        </Button>
                        <Button type="submit" className="btn btn-solid-blue fw-bold" disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Running...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-play me-2"></i> Run CPM
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Actual Start Date Modal */}
            <Modal show={showActualStartModal} onHide={() => { setActualStartValidated(false); setShowActualStartModal(false); setSelectedActivity(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedActivity?.actual_start ? "Edit" : "Set"} Actual Start Date</Modal.Title>
                </Modal.Header>
                <Form noValidate validated={actualStartValidated} onSubmit={handleSaveActualStart}>
                    <Modal.Body>
                        {selectedActivity && (
                            <div className="mb-3 p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                                <strong>Activity:</strong> {selectedActivity.label}
                            </div>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Actual Start Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="date"
                                value={actualStartDate}
                                onChange={(e) => setActualStartDate(e.target.value)}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Please select an actual start date.
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setActualStartValidated(false); setShowActualStartModal(false); setSelectedActivity(null); }} disabled={savingActualDate}>
                            Cancel
                        </Button>
                        <Button type="submit" className="btn btn-solid-blue fw-bold" disabled={savingActualDate}>
                            {savingActualDate ? (
                                <>
                                    <Spinner animation="border" size="sm" className="mr-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-save mr-2"></i> Save
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Actual Finish Date Modal */}
            <Modal show={showActualFinishModal} onHide={() => { setActualFinishValidated(false); setShowActualFinishModal(false); setSelectedActivity(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedActivity?.actual_finish ? "Edit" : "Set"} Actual Finish Date</Modal.Title>
                </Modal.Header>
                <Form noValidate validated={actualFinishValidated} onSubmit={handleSaveActualFinish}>
                    <Modal.Body>
                        {selectedActivity && (
                            <div className="mb-3 p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                                <div><strong>Activity:</strong> {selectedActivity.label}</div>
                                <div className="mt-2"><strong>Actual Start:</strong> {selectedActivity.actual_start || "Not set"}</div>
                            </div>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Actual Finish Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="date"
                                value={actualFinishDate}
                                onChange={(e) => setActualFinishDate(e.target.value)}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Please select an actual finish date.
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                Finish date must be greater than or equal to the start date.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setActualFinishValidated(false); setShowActualFinishModal(false); setSelectedActivity(null); }} disabled={savingActualDate}>
                            Cancel
                        </Button>
                        <Button type="submit" className="btn btn-solid-blue fw-bold" disabled={savingActualDate}>
                            {savingActualDate ? (
                                <>
                                    <Spinner animation="border" size="sm" className="mr-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-save mr-2"></i> Save
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Labour Allocation Error Modal - Enhanced */}
            <Modal 
                show={showAllocationErrorModal} 
                onHide={() => setShowAllocationErrorModal(false)} 
                centered
                className="labour-allocation-error-modal"
            >
                <Modal.Header closeButton className="border-0 pb-0" />
                <Modal.Body className="text-center">
                    {/* Icon with pulse animation */}
                    <div className="error-icon-wrapper">
                        <div className="error-icon-circle">
                            <i className="fa fa-exclamation-triangle"></i>
                        </div>
                    </div>

                    {/* Title */}
                    <h4 className="error-title">
                        Labour Allocation Error
                    </h4>
                    
                    {/* Visual divider */}
                    <div className="error-divider"></div>

                    {/* Error message */}
                    <p className="error-message">
                        {allocationErrorMessage}
                    </p>

                    {/* Action button */}
                    <button
                        className="error-action-btn"
                        onClick={() => setShowAllocationErrorModal(false)}
                    >
                        <i className="fa fa-check"></i>
                        I Understand
                    </button>
                </Modal.Body>
            </Modal>

            {/* Milestone Create/Edit Modal */}
            <Modal 
                show={showMilestoneModal} 
                onHide={() => { setMilestoneFormValidated(false); setDateValidationError(""); setShowMilestoneModal(false); }} 
                centered
                size="lg"
                className="milestone-modal"
            >
                <Modal.Header className="milestone-modal-header" closeButton>
                    <div className="milestone-modal-header-content">
                        
                        <div className="milestone-modal-title-wrapper">
                            <Modal.Title className="milestone-modal-title">
                                {editingMilestone ? 'Edit Milestone' : 'Create New Milestone'}
                            </Modal.Title>
                            <div className="milestone-modal-subtitle">
                                {editingMilestone 
                                    ? 'Update milestone details to track project progress' 
                                    : 'Add a new milestone to track project progress'}
                            </div>
                        </div>
                    </div>
                   
                </Modal.Header>
                <Form noValidate validated={milestoneFormValidated} onSubmit={handleSaveMilestone}>
                    <Modal.Body className="milestone-modal-body">
                        <Form.Group className="milestone-form-group mb-4 milestone-form-group-horizontal">
                            <Form.Label className="milestone-form-label fw-bold">
                                Milestone Name <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="milestone-input-wrapper">
                                
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., Foundation Complete, Structure Phase 1"
                                    value={milestoneForm.name}
                                    onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                                    required
                                    maxLength={100}
                                    className={`milestone-form-control ${milestoneFormValidated && !milestoneForm.name ? 'is-invalid' : ''} ${milestoneForm.name.length >= 100 ? 'is-invalid' : ''}`}
                                    aria-invalid={milestoneFormValidated && !milestoneForm.name}
                                />
                            </div>
                            <Form.Control.Feedback type="invalid" className="milestone-form-feedback">
                                {milestoneForm.name.length >= 100 ? 'Milestone name cannot exceed 100 characters.' : 'Please provide a milestone name.'}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="milestone-form-group mb-4">
                            <Form.Label className="milestone-form-label fw-bold">Description</Form.Label>
                            <div className="milestone-input-wrapper">
                                <div className="milestone-input-icon milestone-input-icon-desc">
                                    <i className="fa fa-file"></i>
                                </div>
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    placeholder="Optional description of this milestone..."
                                    value={milestoneForm.description}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value.length <= 500) {
                                            setMilestoneForm({ ...milestoneForm, description: value });
                                        }
                                    }}
                                    className={`milestone-form-control milestone-form-textarea ${milestoneForm.description.length > 500 ? 'is-invalid' : ''}`}
                                    aria-invalid={milestoneForm.description.length > 500}
                                />
                            </div>
                            <div className="milestone-char-counter-wrapper">
                                <div className="milestone-char-counter">
                                    <span className="milestone-char-count">{milestoneForm.description.length} / 500 characters</span>
                                    <div className="milestone-char-aa" aria-hidden="true">Aa</div>
                                </div>
                                {milestoneForm.description.length > 500 && (
                                    <div className="milestone-char-error">
                                        <i className="fa fa-exclamation-circle mr-1"></i>
                                        Description exceeds 500 character limit
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                       
                        <Form.Group className="milestone-form-group mb-4 milestone-form-group-horizontal">
                            <Form.Label className="milestone-form-label fw-bold">Start Date <span className="text-danger">*</span></Form.Label>
                            <div className="milestone-input-wrapper">
                                <div className="milestone-input-icon">
                                    <i className="fa fa-calendar"></i>
                                </div>
                                <Form.Control
                                    type="date"
                                    value={milestoneForm.start_date}
                                    onChange={(e) => {
                                        setMilestoneForm({ ...milestoneForm, start_date: e.target.value });
                                        if (dateValidationError) setDateValidationError("");
                                    }}
                                    className={`milestone-form-control ${milestoneFormValidated && !milestoneForm.start_date ? 'is-invalid' : ''}`}
                                    required
                                />
                            </div>
                            <Form.Control.Feedback type="invalid" className="milestone-form-feedback">
                                Please select a start date.
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="milestone-form-group mb-4 milestone-form-group-horizontal">
                            <Form.Label className="milestone-form-label fw-bold">End Date <span className="text-danger">*</span></Form.Label>
                            <div className="milestone-input-wrapper">
                                <div className="milestone-input-icon">
                                    <i className="fa fa-calendar"></i>
                                </div>
                                <Form.Control
                                    type="date"
                                    value={milestoneForm.end_date}
                                    onChange={(e) => {
                                        setMilestoneForm({ ...milestoneForm, end_date: e.target.value });
                                        if (dateValidationError) setDateValidationError("");
                                    }}
                                    className={`milestone-form-control ${milestoneFormValidated && !milestoneForm.end_date ? 'is-invalid' : ''}`}
                                    required
                                />
                            </div>
                            <Form.Control.Feedback type="invalid" className="milestone-form-feedback">
                                Please select an end date.
                            </Form.Control.Feedback>
                        </Form.Group>

                        {dateValidationError && (
                            <div className="milestone-form-group mb-4 milestone-date-error">
                                <div className="alert alert-danger py-2 px-3 mb-0" role="alert">
                                    <i className="fa fa-exclamation-circle mr-2"></i>
                                    {dateValidationError}
                                </div>
                            </div>
                        )}
                       
                    </Modal.Body>
                    <Modal.Footer className="milestone-modal-footer">
                        <Button 
                            variant="secondary" 
                            className="milestone-modal-btn-cancel"
                            onClick={() => { setMilestoneFormValidated(false); setDateValidationError(""); setShowMilestoneModal(false); }}
                            disabled={milestoneSaving}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            className="milestone-modal-btn-create"
                            disabled={milestoneSaving || !milestoneForm.name.trim() || milestoneForm.description.length > 500}
                        >
                            {milestoneSaving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="mr-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-save mr-2"></i>
                                    {editingMilestone ? 'Update' : 'Create'} Milestone
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Task Link Modal */}
            <Modal 
                show={showTaskLinkModal} 
                onHide={() => { setShowTaskLinkModal(false); setSelectedMilestoneForTasks(null); setTaskSearchQuery(""); }} 
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Link Tasks to Milestone
                        {selectedMilestoneForTasks && (
                            <div className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 'normal', marginTop: '0.25rem' }}>
                                {selectedMilestoneForTasks.name}
                            </div>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {availableTasks.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="fa fa-tasks fa-3x text-muted mb-3"></i>
                            <p className="text-muted">No tasks available in CPM data</p>
                        </div>
                    ) : (
                        <>
                            {/* Search Input */}
                            <div className="mb-3">
                                <Form.Control
                                    type="text"
                                    placeholder="Search tasks by name or stage..."
                                    value={taskSearchQuery}
                                    onChange={(e) => setTaskSearchQuery(e.target.value)}
                                    className="form-control-sm"
                                />
                            </div>

                            {/* Filtered Task List grouped by Phase */}
                            {(() => {
                                // Filter tasks based on search query
                                const filteredTasks = debouncedSearchQuery.trim() 
                                    ? availableTasks.filter(task => 
                                        task.label.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                        (task.stage_label && task.stage_label.toLowerCase().includes(debouncedSearchQuery.toLowerCase()))
                                    )
                                    : availableTasks;

                                if (filteredTasks.length === 0) {
                                    return (
                                        <div className="text-center py-4">
                                            <i className="fa fa-search fa-3x text-muted mb-3"></i>
                                            <p className="text-muted">No matching tasks found</p>
                                        </div>
                                    );
                                }

                                const tasksByPhase = {};
                                filteredTasks.forEach(task => {
                                    const phase = task.phase || 'Other';
                                    if (!tasksByPhase[phase]) {
                                        tasksByPhase[phase] = [];
                                    }
                                    tasksByPhase[phase].push(task);
                                });

                                return Object.entries(tasksByPhase).map(([phase, tasks]) => (
                                    <div key={phase} className="mb-4">
                                        <h6 className="fw-bold mb-3" style={{ color: '#374151', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem' }}>
                                            <i className="fa fa-layer-group mr-2"></i>
                                            {phase}
                                            <Badge bg="secondary" className="ml-2">{tasks.length}</Badge>
                                        </h6>
                                        <div className="task-selection-list">
                                            {tasks.map(task => {
                                                const isSelected = selectedTaskIds.includes(task.id);
                                                const taskDetails = getTaskById(task.id);
                                                const isCritical = taskDetails?.critical;
                                                const isAssignedToOther = task.assignedToOtherMilestone;

                                                return (
                                                    <div
                                                        key={task.id}
                                                        className={`task-selection-item ${isSelected ? 'selected' : ''} ${isAssignedToOther ? 'assigned-elsewhere' : ''}`}
                                                        onClick={isAssignedToOther ? undefined : () => handleToggleTask(task.id)}
                                                        style={{
                                                            padding: '0.75rem',
                                                            marginBottom: '0.5rem',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '6px',
                                                            cursor: isAssignedToOther ? 'not-allowed' : 'pointer',
                                                            backgroundColor: isAssignedToOther ? '#f9fafb' : (isSelected ? '#eef2ff' : '#fff'),
                                                            borderColor: isAssignedToOther ? '#e5e7eb' : (isSelected ? '#7c3aed' : '#e5e7eb'),
                                                            opacity: isAssignedToOther ? 0.6 : 1,
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                disabled={isAssignedToOther}
                                                                onChange={() => {}}
                                                                className="mr-3"
                                                            />
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                                    <span className="fw-bold">{task.label}</span>
                                                                    {isCritical && (
                                                                        <Badge bg="danger" className="ml-1">
                                                                            <i className="fa fa-exclamation-triangle mr-1"></i>
                                                                            Critical
                                                                        </Badge>
                                                                    )}
                                                                    {isAssignedToOther && (
                                                                        <Badge bg="warning" className="ml-1" style={{ cursor: 'help' }} 
                                                                               data-bs-toggle="tooltip" 
                                                                               data-bs-placement="top" 
                                                                               title={`Assigned to "${task.assignedToOtherMilestone}"`}>
                                                                            <i className="fa fa-lock mr-1"></i>
                                                                            Assigned
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                {task.stage_label && (
                                                                    <small className="text-muted">
                                                                        <i className="fa fa-tags mr-1"></i>
                                                                        {task.stage_label}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ));
                            })()}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => { setShowTaskLinkModal(false); setSelectedMilestoneForTasks(null); setTaskSearchQuery(""); }}
                        disabled={taskLinkingSaving}
                    >
                        Cancel
                    </Button>
                    <Button 
                        className="btn btn-solid-blue fw-bold"
                        onClick={handleSaveTaskLinks}
                        disabled={taskLinkingSaving || availableTasks.length === 0}
                    >
                        {taskLinkingSaving ? (
                            <>
                                <Spinner animation="border" size="sm" className="mr-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-link mr-2"></i>
                                Save Task Links ({selectedTaskIds.length})
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Milestone Confirmation Modal */}
            <Modal 
                show={showDeleteMilestoneModal} 
                onHide={() => { setShowDeleteMilestoneModal(false); setMilestoneToDelete(null); }} 
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa fa-exclamation-triangle text-warning mr-2"></i>
                        Delete Milestone
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-2">
                        <i className="fa fa-flag fa-3x text-warning mb-3"></i>
                        <h5>Are you sure you want to delete this milestone?</h5>
                        <p className="text-muted">This action cannot be undone. All linked tasks will be unlinked from this milestone.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => { setShowDeleteMilestoneModal(false); setMilestoneToDelete(null); }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="danger"
                        className="fw-bold"
                        onClick={handleConfirmDeleteMilestone}
                    >
                        <i className="fa fa-trash mr-2"></i> Delete Milestone
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Unlink Task Confirmation Modal */}
            <Modal 
                show={showUnlinkTaskModal} 
                onHide={() => { setShowUnlinkTaskModal(false); setTaskToUnlink(null); }} 
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa fa-exclamation-triangle text-warning mr-2"></i>
                        Unlink Task
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-2">
                        <i className="fa fa-unlink fa-3x text-warning mb-3"></i>
                        <h5>Are you sure you want to unlink this task from the milestone?</h5>
                        <p className="text-muted">This action cannot be undone.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => { setShowUnlinkTaskModal(false); setTaskToUnlink(null); }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="danger"
                        className="fw-bold"
                        onClick={handleConfirmUnlinkTask}
                    >
                        <i className="fa fa-unlink mr-2"></i> Unlink Task
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Scroll to Top Button */}
            {showScrollTop && (
                <button
                    className="scroll-top-btn"
                    onClick={() => {
                        const contentBox = document.querySelector('.content-box');
                        if (contentBox) {
                            contentBox.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                    }}
                    title="Scroll to top"
                >
                    <i className="fa fa-arrow-up"></i>
                </button>
            )}
        </div>
    );
}
